import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ImageUploader } from "./ImageUploader";
import { LiveStatusIndicator } from "./LiveStatusIndicator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SLOTS = ["new_deals", "after_hours", "weekender", "locals_only", "vip_only", "hidden_gems"];

// Searchable picker for tagging a popup/carousel/etc to an active business.
// Calls /api/admin/businesses?q=... and shows a 5-row dropdown of matches.
function BusinessPicker({ value, onChange, headers, testIdPrefix = "biz-picker" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [resolvedName, setResolvedName] = useState("");
  const [busy, setBusy] = useState(false);

  // Resolve current value -> business name (for the chip)
  useEffect(() => {
    let alive = true;
    if (!value) { setResolvedName(""); return; }
    axios.get(`${API}/admin/businesses?slim=true&q=`, { headers })
      .then(r => {
        if (!alive) return;
        const found = (r.data || []).find(b => b.id === value);
        setResolvedName(found?.name || "");
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [value, headers]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    setBusy(true);
    const t = setTimeout(() => {
      axios.get(`${API}/admin/businesses?slim=true&q=${encodeURIComponent(term)}`, { headers })
        .then(r => setResults((r.data || []).slice(0, 8)))
        .catch(() => setResults([]))
        .finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, headers]);

  if (value && !open) {
    return (
      <div className="flex items-center gap-2">
        <div data-testid={`${testIdPrefix}-chip`} className="flex-1 px-3 py-2.5 bg-gold/10 border border-gold/40 rounded-sm flex items-center gap-2">
          <span className="text-gold text-[10px] uppercase tracking-widest font-display">Linked</span>
          <span className="text-sm flex-1 truncate">{resolvedName || value.slice(0, 8) + "…"}</span>
        </div>
        <button type="button" data-testid={`${testIdPrefix}-change`} onClick={() => { setOpen(true); setQ(""); setResults([]); }} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2.5 border border-white/15 hover:border-gold hover:text-gold rounded-sm">Change</button>
        <button type="button" data-testid={`${testIdPrefix}-clear`} onClick={() => onChange("")} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2.5 border border-white/15 hover:border-red-400 hover:text-red-400 rounded-sm">Unlink</button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input data-testid={`${testIdPrefix}-input`} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Search active businesses (type 2+ chars)…" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
      {open && q.trim().length >= 2 && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-surface border border-gold/30 rounded-sm shadow-xl">
          {busy && <div className="px-3 py-2 text-muted text-xs font-serif-accent">Searching…</div>}
          {!busy && results.length === 0 && <div className="px-3 py-3 text-muted text-xs font-serif-accent">No matches.</div>}
          {results.map(b => (
            <button key={b.id} type="button" data-testid={`${testIdPrefix}-result-${b.id}`} onClick={() => { onChange(b.id); setOpen(false); setQ(""); setResolvedName(b.name); }} className="block w-full text-left px-3 py-2.5 border-b border-white/5 last:border-0 hover:bg-bg/50">
              <div className="font-display text-sm truncate">{b.name}</div>
              <div className="text-muted text-[10px] uppercase tracking-widest font-display">{b.category} {b.subcategory ? `· ${b.subcategory}` : ""} {b.subscription_status ? `· ${b.subscription_status}` : ""}</div>
            </button>
          ))}
        </div>
      )}
      {open && q.trim().length < 2 && (
        <div className="text-[10px] text-muted mt-1 font-serif-accent">Type 2+ characters to search · or paste a UUID directly into the field above</div>
      )}
    </div>
  );
}

// Cascading sub-category picker — pulls suggestions for the chosen category from
// /api/categories/subcategories. Falls back to free-text input if user wants
// something custom (datalist pattern).

// Live homepage-matching preview of what a category card will look like on the
// front page — mirrors the CategoryBar component in App.js (same aspect ratio,
// gradient overlay, gold tag, big display title). Renders instantly when admin
// uploads a new image, so they can see the result before saving.
function CategoryCardPreview({ img, title, tag, testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 rounded-sm overflow-hidden bg-bg">
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display px-3 py-2 border-b border-white/5 bg-bg/60">Homepage preview</div>
      <div className="relative w-full h-40 overflow-hidden bg-bg">
        {img ? (
          <img src={img} alt={title || "preview"} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-muted text-xs font-serif-accent">Upload an image to preview</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10 pointer-events-none" />
        <div className="absolute inset-0 flex items-end p-5">
          <div>
            <div className="font-serif-accent text-gold text-xs mb-1">{tag || "\u00A0"}</div>
            <div className="font-display uppercase text-2xl tracking-tight text-white drop-shadow-xl">{title || "Category title"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}


function SubcategoryPicker({ category, value, onChange, testId, className = "" }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!category) { setOpts([]); return; }
    let alive = true;
    axios.get(`${API}/categories/subcategories?category=${encodeURIComponent(category)}`)
      .then(r => { if (alive) setOpts(r.data?.subcategories || []); })
      .catch(() => { if (alive) setOpts([]); });
    return () => { alive = false; };
  }, [category]);

  const listId = `subcat-list-${testId || category || "none"}`;
  return (
    <>
      <input
        list={listId}
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={category ? "Sub-category (pick or type)" : "Pick a category first"}
        disabled={!category}
        className={`bg-bg border border-white/10 p-3 rounded-sm text-sm disabled:opacity-50 ${className}`}
      />
      <datalist id={listId}>
        {opts.map(o => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

// Picks an attachment target for a photo, varies the data source by `kind`.
// kind: business | category_override | popup_ad | ad | banner_ad | carousel_ad | happening
function PhotoAttachTargetPicker({ kind, onPick, busy, bizs, bizSearch, setBizSearch, headers }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    setItems(null);
    setErr("");
    setQ("");
    if (kind === "business") {
      // bizs already in parent state; nothing to fetch.
      setItems(null);
      return;
    }
    const endpoints = {
      category_override: `${API}/categories/full`,           // [{key,title,img,...}]
      popup_ad:          `${API}/admin/popup-ads`,            // [{id,title,category,body,...}]
      ad:                `${API}/admin/ads/all`,             // 24/7 ads list
      banner_ad:         `${API}/admin/banner-ads`,           // {<slot>:{current,lineup}}
      carousel_ad:       `${API}/admin/carousel-ads`,         // [{slot,id,headline,...}]
      happening:         `${API}/admin/happening`,            // [{id,description,...}]
    };
    const url = endpoints[kind];
    if (!url) return;
    const isAdmin = url.includes("/admin/");
    axios.get(url, { headers: isAdmin ? headers() : undefined })
      .then(r => {
        const data = r.data;
        // Normalize each kind into a flat [{id, title, subtitle, thumb?}] list.
        if (kind === "category_override") {
          setItems((data || []).map(c => ({ id: c.key, title: c.title || c.key, subtitle: c.tag || "Category", thumb: c.img })));
        } else if (kind === "popup_ad") {
          setItems((data || []).map(p => ({ id: p.id, title: p.title || "(no title)", subtitle: p.category || "popup", thumb: p.image })));
        } else if (kind === "ad") {
          setItems((data || []).map(a => ({ id: a.id, title: a.headline || a.business_name || "(no headline)", subtitle: a.category || "24/7", thumb: a.image_url })));
        } else if (kind === "banner_ad") {
          const flat = [];
          Object.entries(data || {}).forEach(([slot, slotData]) => {
            if (slotData?.current?.id) flat.push({ id: slotData.current.id, title: slotData.current.headline || slotData.current.business_name, subtitle: `${slot} (current)`, thumb: slotData.current.image_url });
            (slotData?.lineup || []).forEach(b => {
              if (b.id && b.id !== slotData?.current?.id) flat.push({ id: b.id, title: b.headline || b.business_name, subtitle: `${slot} (queued)`, thumb: b.image_url });
            });
          });
          setItems(flat);
        } else if (kind === "carousel_ad") {
          setItems((data || []).filter(c => c.id).map(c => ({ id: c.id, title: c.headline || `Slot ${c.slot}`, subtitle: c.slot, thumb: c.image_url })));
        } else if (kind === "happening") {
          setItems((data || []).map(e => ({ id: e.id, title: (e.description || "").slice(0, 60) || "(no description)", subtitle: e.business_name || "happening", thumb: e.image })));
        } else {
          setItems([]);
        }
      })
      .catch(e => setErr(e.response?.data?.detail || e.message || "Could not load targets"));
  }, [kind, headers]);

  if (kind === "business") {
    const filtered = (bizs || []).filter(b => !bizSearch.trim() || (b.name || "").toLowerCase().includes(bizSearch.toLowerCase())).slice(0, 50);
    return (
      <>
        <input
          data-testid="photo-attach-search"
          value={bizSearch}
          onChange={(e) => setBizSearch(e.target.value)}
          placeholder="Search a business…"
          className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm"
        />
        <div className="border border-white/10 rounded-sm max-h-[40vh] overflow-y-auto">
          {filtered.map(b => (
            <button
              key={b.id}
              data-testid={`photo-attach-pick-${b.id}`}
              onClick={() => onPick(b.id)}
              disabled={busy}
              className="w-full text-left p-3 border-b border-white/5 hover:bg-gold/10 transition-colors disabled:opacity-50 flex items-center gap-3"
            >
              {b.img && <div className="w-12 h-12 shrink-0 bg-cover bg-center rounded-sm border border-white/10" style={{ backgroundImage: `url('${b.img}')` }} />}
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm truncate">{b.name}</div>
                <div className="text-[10px] text-muted">{b.category}{b.subcategory ? ` · ${b.subcategory}` : ""}</div>
              </div>
            </button>
          ))}
          {(bizs || []).length === 0 && <div className="p-6 text-center text-muted text-sm font-serif-accent">No businesses loaded — close and re-open the tab.</div>}
        </div>
      </>
    );
  }

  const filtered = (items || []).filter(it => !q.trim() || (it.title || "").toLowerCase().includes(q.toLowerCase()) || (it.subtitle || "").toLowerCase().includes(q.toLowerCase())).slice(0, 80);
  return (
    <>
      <input
        data-testid="photo-attach-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${kind.replace("_", " ")}…`}
        className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm"
      />
      {err && <div className="text-red-400 text-sm font-serif-accent">{err}</div>}
      <div className="border border-white/10 rounded-sm max-h-[40vh] overflow-y-auto">
        {items === null && !err && <div className="p-6 text-center text-muted text-sm font-serif-accent">Loading…</div>}
        {items !== null && filtered.length === 0 && <div className="p-6 text-center text-muted text-sm font-serif-accent">Nothing to attach to.</div>}
        {filtered.map(it => (
          <button
            key={it.id}
            data-testid={`photo-attach-pick-${it.id}`}
            onClick={() => onPick(it.id)}
            disabled={busy}
            className="w-full text-left p-3 border-b border-white/5 hover:bg-gold/10 transition-colors disabled:opacity-50 flex items-center gap-3"
          >
            {it.thumb && <div className="w-12 h-12 shrink-0 bg-cover bg-center rounded-sm border border-white/10" style={{ backgroundImage: `url('${it.thumb.startsWith("/api/") ? `${BACKEND_URL}${it.thumb}` : it.thumb}')` }} />}
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm truncate">{it.title}</div>
              <div className="text-[10px] text-muted">{it.subtitle}</div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}



export default function AdminPage() {
  const [key, setKey] = useState(localStorage.getItem("zc_admin_key") || "");
  const [tab, setTab] = useState("claims");
  const [claims, setClaims] = useState(null);
  const [err, setErr] = useState("");
  // Auto-dismiss any "Error loading X" message after 8 seconds — without this,
  // a transient Cloudflare 502 leaves a red error banner on screen forever and
  // makes the user think the entire admin is broken. They had to refresh the
  // whole page just to clear it. Now errors gracefully fade away on their own.
  useEffect(() => {
    if (!err) return;
    const t = setTimeout(() => setErr(""), 8000);
    return () => clearTimeout(t);
  }, [err]);
  const [categories, setCategories] = useState([]); // [{key,title,...}] from /api/categories/full
  const [categoriesLoadState, setCategoriesLoadState] = useState("idle"); // idle | loading | loaded | error
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ category: "whatsnew", subcategory: "", address: "Las Vegas, NV", offer: "NEW LISTING", img: "", desc: "" });

  // Gift state
  const [bizSearch, setBizSearch] = useState("");
  const [bizs, setBizs] = useState([]);
  const [selBiz, setSelBiz] = useState(null);
  const [giftMsg, setGiftMsg] = useState("");
  const [subMonths, setSubMonths] = useState(3);
  const [subPlan, setSubPlan] = useState("pro");
  const [popupCat, setPopupCat] = useState("whatsnew");
  const [popupWeeks, setPopupWeeks] = useState(2);
  const [bannerSlot, setBannerSlot] = useState("after_hours");
  const [bannerWeeks, setBannerWeeks] = useState(4);

  // Affiliates state
  const [affs, setAffs] = useState([]);
  const [payoutFor, setPayoutFor] = useState(null);
  const [affSearch, setAffSearch] = useState("");
  const [expandedAff, setExpandedAff] = useState(null);
  const [copiedAff, setCopiedAff] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("paypal");
  const [payoutNote, setPayoutNote] = useState("");

  // Re-fetch categories — called after admin adds/edits/deletes one so dropdowns refresh immediately.
  // Also called when the "Seed a new business" form opens, so transient 502s never leave
  // the dropdown empty for long.
  const reloadCategoriesList = async () => {
    setCategoriesLoadState("loading");
    // Try up to 3x with simple backoff so a single Cloudflare/backend 502 doesn't leave the
    // admin with an empty dropdown.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const r = await axios.get(`${API}/categories/full?_=${Date.now()}`);
        const arr = Array.isArray(r.data) ? r.data : [];
        setCategories(arr.map(c => ({ key: c.key, title: c.title || c.key, tag: c.tag || "" })));
        setCategoriesLoadState("loaded");
        return;
      } catch (e) {
        if (attempt === 2) {
          setCategoriesLoadState("error");
          return;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise(res => setTimeout(res, 600 * (attempt + 1)));
      }
    }
  };

  const [adminToken, setAdminToken] = useState(localStorage.getItem("zc_admin_token") || "");
  const [adminMe, setAdminMe] = useState(null);
  const [loginUser, setLoginUser] = useState("");
  const [loginPwd, setLoginPwd] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [bootstrapMode, setBootstrapMode] = useState(false);
  const [bootstrapKey, setBootstrapKey] = useState("");

  useEffect(() => {
    // Categories are only needed AFTER admin sign-in (used by dropdowns inside
    // the console). Skipping this on the login screen so the form is interactive
    // immediately — previously the 3x retry-with-backoff on /categories/full
    // could block the login UX for several seconds on a flaky network.
    if (!adminToken && !key) return;
    reloadCategoriesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, key]);

  // Headers helper — Bearer admin session preferred, fall back to master key
  const headers = () => {
    if (adminToken) return { Authorization: `Bearer ${adminToken}` };
    if (key) return { "x-admin-key": key };
    return {};
  };

  // Fetch /admin/me when token changes — used to gate UI based on role
  useEffect(() => {
    if (!adminToken && !key) { setAdminMe(null); return; }
    axios.get(`${API}/admin/me`, { headers: headers() })
      .then(r => { setAdminMe(r.data); loadClaims(); })
      .catch(() => { setAdminMe(null); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, key]);

  const doLogin = async () => {
    setLoginErr(""); setLoginBusy(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { username: loginUser.trim().toLowerCase(), password: loginPwd });
      localStorage.setItem("zc_admin_token", r.data.token);
      localStorage.setItem("zc_admin_username", r.data.username);
      setAdminToken(r.data.token);
      setLoginPwd(""); setLoginUser("");
      if (r.data.must_change_password) {
        alert("Welcome — please change your password under Settings.");
      }
    } catch (e) {
      setLoginErr(e.response?.data?.detail || "Login failed");
    }
    setLoginBusy(false);
  };

  const doBootstrap = async () => {
    setLoginErr(""); setLoginBusy(true);
    try {
      await axios.post(`${API}/admin/master-bootstrap`, { username: loginUser.trim().toLowerCase(), password: loginPwd }, { headers: { "x-admin-key": bootstrapKey } });
      // auto-login
      const r = await axios.post(`${API}/admin/login`, { username: loginUser.trim().toLowerCase(), password: loginPwd });
      localStorage.setItem("zc_admin_token", r.data.token);
      localStorage.setItem("zc_admin_username", r.data.username);
      setAdminToken(r.data.token);
      setBootstrapMode(false);
      setBootstrapKey(""); setLoginPwd("");
    } catch (e) {
      setLoginErr(e.response?.data?.detail || "Bootstrap failed — check master key");
    }
    setLoginBusy(false);
  };

  const doLogout = async () => {
    try { await axios.post(`${API}/admin/logout`, null, { headers: headers() }); } catch {}
    localStorage.removeItem("zc_admin_token");
    localStorage.removeItem("zc_admin_username");
    setAdminToken("");
    setAdminMe(null);
  };

  const loadAffs = async () => {
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/affiliates`, { headers: headers() });
      setAffs(r.data);
    } catch (e) {
      setErr(e.response?.status === 401 ? "Session expired — please log in again" : "Error loading affiliates");
    }
  };

  const submitPayout = async (a) => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt <= 0) return alert("Enter a positive amount");
    if (amt > a.balance_dollars) return alert(`Exceeds balance ($${a.balance_dollars.toFixed(2)})`);
    try {
      await axios.post(`${API}/admin/affiliates/payout`, {
        affiliate_userId: a.userId,
        amount_dollars: amt,
        method: payoutMethod,
        note: payoutNote,
      }, { headers: headers() });
      setPayoutFor(null); setPayoutAmount(""); setPayoutNote("");
      loadAffs();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const [adminUsers, setAdminUsers] = useState(null);
  const [newAdminUser, setNewAdminUser] = useState("");
  const [newAdminPwd, setNewAdminPwd] = useState("");
  const [newAdminRole, setNewAdminRole] = useState("staff");
  const [teamMsg, setTeamMsg] = useState("");
  const [myCurrPwd, setMyCurrPwd] = useState("");
  const [myNewPwd, setMyNewPwd] = useState("");

  const loadAdminUsers = async () => {
    try {
      const r = await axios.get(`${API}/admin/users`, { headers: headers() });
      setAdminUsers(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Error loading team"); }
  };

  const createAdminUser = async () => {
    setTeamMsg("");
    try {
      await axios.post(`${API}/admin/users`, { username: newAdminUser.trim().toLowerCase(), password: newAdminPwd, role: newAdminRole }, { headers: headers() });
      setTeamMsg(`✓ Created — share with ${newAdminUser}: username "${newAdminUser.trim().toLowerCase()}" + that initial password. They'll be prompted to change it on first login.`);
      setNewAdminUser(""); setNewAdminPwd("");
      loadAdminUsers();
    } catch (e) { setTeamMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const resetAdminPassword = async (username) => {
    const pwd = window.prompt(`Set new password for ${username} (8+ chars)`);
    if (!pwd || pwd.length < 8) return;
    try {
      await axios.post(`${API}/admin/users/${username}/reset-password`, { new_password: pwd }, { headers: headers() });
      alert(`✓ Password reset for ${username}. Share new password with them — they'll be required to change on next login.`);
      loadAdminUsers();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const deleteAdminUser = async (username) => {
    if (!window.confirm(`Remove ${username}? They will lose admin access immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${username}`, { headers: headers() });
      loadAdminUsers();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const changeMyPassword = async () => {
    if (myNewPwd.length < 8) { alert("New password must be 8+ chars"); return; }
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: myCurrPwd, new_password: myNewPwd }, { headers: headers() });
      alert("✓ Password updated. Sign in again with your new password.");
      setMyCurrPwd(""); setMyNewPwd("");
      doLogout();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const [pending, setPending] = useState(null);
  // All listings (admin CRUD)
  const [allListings, setAllListings] = useState(null);
  const [listingFilter, setListingFilter] = useState("");
  const [listingEditing, setListingEditing] = useState(null); // {id, fields}
  const [newBizForm, setNewBizForm] = useState({ name: "", category: "", subcategory: "", desc: "", offer: "", img: "", address: "", phone: "", website: "" });
  const [showNewBiz, setShowNewBiz] = useState(false);
  // Pending events (public submissions)
  const [eventsPending, setEventsPending] = useState(null);
  // Pricing
  const [pricing, setPricing] = useState(null);
  const [pricingMsg, setPricingMsg] = useState("");
  // Pop-up ad creation
  const [newPopup, setNewPopup] = useState({ category: "", title: "", body: "", image: "", cta_text: "Visit", cta_url: "", business_id: "" });
  const [popupMsg, setPopupMsg] = useState("");
  // Pop-up ads list + edit
  const [popups, setPopups] = useState(null);
  const [popupSearch, setPopupSearch] = useState("");
  const [popupEditing, setPopupEditing] = useState(null); // {id, category, title, body, image, cta_text, cta_url, business_id}
  const [popupBusy, setPopupBusy] = useState(false);
  const [popupModalErr, setPopupModalErr] = useState("");
  // Carousel slot management
  const [carousel, setCarousel] = useState(null);
  const [carouselEditing, setCarouselEditing] = useState(null); // {slot, headline, body, image_url, cta_text, cta_url, business_id}
  const [carouselMsg, setCarouselMsg] = useState("");
  // Banner tab
  const [banners, setBanners] = useState(null);
  const [bannerEditing, setBannerEditing] = useState(null);
  const [bannerMsg, setBannerMsg] = useState("");
  // Ad approval queue
  const [pendingAds, setPendingAds] = useState(null);
  const [pendingMsg, setPendingMsg] = useState("");
  // Category management
  const [catalogue, setCatalogue] = useState(null);
  const [catEditing, setCatEditing] = useState(null); // category being edited
  const [catNew, setCatNew] = useState({ key: "", title: "", tag: "", img: "", subcategories: "" });
  const [catMsg, setCatMsg] = useState("");
  const [catBusy, setCatBusy] = useState(false);

  // Banner slot title edit (admin can rename "Hot Deals" → anything)
  const [slotMetaEdit, setSlotMetaEdit] = useState(null);
  const saveSlotMeta = async () => {
    if (!slotMetaEdit) return;
    try {
      await axios.put(`${API}/admin/banner-slot-meta/${slotMetaEdit.slot}`, {
        label: slotMetaEdit.label, tag: slotMetaEdit.tag, blurb: slotMetaEdit.blurb,
      }, { headers: headers() });
      setSlotMetaEdit(null);
      setBannerMsg("✓ Slot title saved");
      await loadBanners();
    } catch (e) { setBannerMsg(e.response?.data?.detail || "Save failed"); }
  };

  // 24/7 ads (db.ads) oversight
  const [ads247, setAds247] = useState(null);
  const [ads247Msg, setAds247Msg] = useState("");
  const [ads247Editing, setAds247Editing] = useState(null);
  const [ads247Search, setAds247Search] = useState("");
  const [newAd247, setNewAd247] = useState({ business_id: "", category: "", headline: "", body: "", image_url: "", cta_text: "Visit", cta_url: "", phone: "" });
  const [newAd247Busy, setNewAd247Busy] = useState(false);
  const [newAd247Msg, setNewAd247Msg] = useState("");
  const [showNewAd247, setShowNewAd247] = useState(false);

  const createAdmin247Ad = async () => {
    if (!newAd247.business_id || !newAd247.category || !newAd247.headline.trim()) {
      setNewAd247Msg("⚠ Business, category and headline are required"); return;
    }
    setNewAd247Busy(true); setNewAd247Msg("");
    try {
      await axios.post(`${API}/admin/ads/create`, newAd247, { headers: headers() });
      setNewAd247Msg("✓ 24/7 ad live — visible on every business detail page in that category");
      setNewAd247({ business_id: "", category: "", headline: "", body: "", image_url: "", cta_text: "Visit", cta_url: "", phone: "" });
      loadAds247();
    } catch (e) { setNewAd247Msg("⚠ " + (e.response?.data?.detail || e.message || "Could not create 24/7 ad")); }
    finally { setNewAd247Busy(false); }
  };
  const loadAds247 = async () => {
    try {
      const r = await axios.get(`${API}/admin/ads/all?slim=true`, { headers: headers() });
      setAds247(r.data || []);
    } catch (e) { setErr(e.response?.data?.detail || "Error loading 24/7 ads"); }
  };
  const openAds247Edit = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/ads/by-id/${id}`, { headers: headers() });
      setAds247Editing(r.data);
    } catch (e) { setAds247Msg(e.response?.data?.detail || "Could not load ad"); }
  };
  const saveAds247Edit = async () => {
    if (!ads247Editing?.id) return;
    setAds247Msg("");
    try {
      await axios.put(`${API}/admin/ads/by-id/${ads247Editing.id}`, {
        headline: ads247Editing.headline,
        body: ads247Editing.body,
        image_url: ads247Editing.image_url,
        cta_text: ads247Editing.cta_text,
        cta_url: ads247Editing.cta_url,
        phone: ads247Editing.phone,
        active: ads247Editing.active,
      }, { headers: headers() });
      setAds247Editing(null);
      setAds247Msg("✓ Saved");
      await loadAds247();
    } catch (e) { setAds247Msg(e.response?.data?.detail || "Save failed"); }
  };
  const deleteAds247 = async (id, biz) => {
    if (!window.confirm(`Permanently delete the 24/7 ad for "${biz}"? The business owner can re-create it.`)) return;
    try {
      await axios.delete(`${API}/admin/ads/by-id/${id}`, { headers: headers() });
      setAds247Msg("✓ Deleted");
      await loadAds247();
    } catch (e) { setAds247Msg(e.response?.data?.detail || "Delete failed"); }
  };

  const loadCatalogue = async () => {
    try {
      const r = await axios.get(`${API}/admin/categories`, { headers: headers() });
      setCatalogue(r.data || []);
    } catch (e) { setErr(e.response?.data?.detail || "Error loading categories"); }
  };

  // ── Admin Analytics ──
  const [analytics, setAnalytics] = useState(null);
  const loadAnalytics = async () => {
    try {
      const r = await axios.get(`${API}/admin/analytics/overview`, { headers: headers() });
      setAnalytics(r.data || {});
    } catch (e) { setErr(e.response?.data?.detail || "Error loading analytics"); }
  };

  const [analyticsBoostBusy, setAnalyticsBoostBusy] = useState(false);
  const [analyticsBoostMsg, setAnalyticsBoostMsg] = useState("");
  const multiplyAnalytics = async (factor = 1.5) => {
    if (!window.confirm(`Multiply ALL business view counts, category-visit counters AND site-visit total by ${factor}×?\n\nOne-time historic-counter boost. Future views count normally on top. Idempotent — safe to click, second call within the same UTC day is a no-op.`)) return;
    setAnalyticsBoostBusy(true); setAnalyticsBoostMsg("");
    try {
      const r = await axios.post(`${API}/admin/analytics/multiply`,
        { factor, targets: ["business_views", "category_visits", "site_visits"] },
        { headers: headers() });
      if (r.data.noop) {
        setAnalyticsBoostMsg(`⓿ No-op — ${r.data.reason || "already applied"}`);
      } else {
        const bits = [];
        if (r.data.businesses_multiplied != null) bits.push(`${r.data.businesses_multiplied} biz ×${factor}`);
        if (r.data.businesses_seeded != null) bits.push(`${r.data.businesses_seeded} seeded`);
        if (r.data.category_keys_multiplied != null) bits.push(`${r.data.category_keys_multiplied} cat keys ×${factor}`);
        if (r.data.site_visit_total != null) bits.push(`site visits → ${r.data.site_visit_total.toLocaleString()}`);
        setAnalyticsBoostMsg(`✓ ${bits.join(" · ")}`);
      }
      loadAnalytics();
    } catch (e) { setAnalyticsBoostMsg("⚠ " + (e.response?.data?.detail || e.message)); }
    finally { setAnalyticsBoostBusy(false); }
  };

  const [newsletterSubs, setNewsletterSubs] = useState(null);
  const [bizEmailCaptures, setBizEmailCaptures] = useState(null);
  const [emailsSubTab, setEmailsSubTab] = useState("newsletter"); // "newsletter" | "business"
  const [emailsSearch, setEmailsSearch] = useState("");

  const loadEmails = async () => {
    setErr("");
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/newsletter-subscribers`, { headers: headers() }),
        axios.get(`${API}/admin/business-emails`, { headers: headers() }),
      ]);
      setNewsletterSubs(a.data);
      setBizEmailCaptures(b.data);
    } catch (e) { setErr(e.response?.data?.detail || "Error loading email lists"); }
  };

  const downloadEmailCsv = (rows, fname) => {
    const header = Object.keys(rows[0] || {}).filter(k => k !== "id");
    const csv = [header.join(",")]
      .concat(rows.map(r => header.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fname;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const seedSiteVisits = async (target = 10000) => {
    if (!window.confirm(`Seed the total site-visit counter to ${target.toLocaleString()}?\n\nSafe mode: only writes if the current counter is BELOW the target. Real visits keep incrementing on top from this point.`)) return;
    setAnalyticsBoostBusy(true); setAnalyticsBoostMsg("");
    try {
      const r = await axios.post(`${API}/admin/analytics/set-site-visits`,
        { value: target, mode: "set_if_below" },
        { headers: headers() });
      if (r.data.noop) {
        setAnalyticsBoostMsg(`⓿ Current site visits (${r.data.current?.toLocaleString?.() ?? r.data.current}) already at or above ${target.toLocaleString()} — nothing to do.`);
      } else {
        setAnalyticsBoostMsg(`✓ Site visits seeded ${r.data.previous?.toLocaleString?.()} → ${r.data.new?.toLocaleString?.()}.`);
      }
      loadAnalytics();
    } catch (e) { setAnalyticsBoostMsg("⚠ " + (e.response?.data?.detail || e.message)); }
    finally { setAnalyticsBoostBusy(false); }
  };

  // ── Admin Site Settings (extra-category price, founding-member cap, plan descriptions, Pro ad discount) ──
  const [settings, setSettings] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState("");
  const loadSettings = async () => {
    try {
      const r = await axios.get(`${API}/admin/settings`, { headers: headers() });
      setSettings(r.data || {});
    } catch (e) { setErr(e.response?.data?.detail || "Error loading settings"); }
  };
  const saveSettings = async (patch) => {
    setSettingsBusy(true); setSettingsMsg("");
    try {
      await axios.put(`${API}/admin/settings`, patch, { headers: headers() });
      setSettingsMsg("✓ Saved — changes are live everywhere");
      await loadSettings();
    } catch (e) { setSettingsMsg("⚠ " + (e.response?.data?.detail || "Save failed")); }
    finally { setSettingsBusy(false); }
  };

  const saveCategoryEdit = async (cat) => {
    setCatBusy(true); setCatMsg("");
    try {
      const payload = {
        key: cat.key,
        title: cat.title,
        tag: cat.tag || "",
        img: cat.img || "",
        custom: !!cat.custom,
      };
      if (cat._subs_csv !== undefined) {
        payload.subcategories = (cat._subs_csv || "").split(",").map(s => s.trim()).filter(Boolean);
      } else if (Array.isArray(cat.subcategories)) {
        payload.subcategories = cat.subcategories;
      }
      await axios.put(`${API}/admin/categories`, payload, { headers: headers() });
      setCatEditing(null);
      setCatMsg("✓ Saved");
      await loadCatalogue();
      await reloadCategoriesList();
    } catch (e) { setCatMsg(e.response?.data?.detail || "Save failed"); }
    finally { setCatBusy(false); }
  };

  const moveCategory = async (idx, dir) => {
    if (!catalogue) return;
    const next = [...catalogue];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setCatalogue(next); // optimistic
    try {
      await axios.post(`${API}/admin/categories/reorder`, { order: next.map(c => c.key) }, { headers: headers() });
      setCatMsg("✓ Order saved");
    } catch (e) { setCatMsg("Reorder failed — reloading"); loadCatalogue(); }
  };

  const addCustomCategory = async () => {
    if (!catNew.key || !catNew.title) { setCatMsg("Key + title required"); return; }
    setCatBusy(true); setCatMsg("");
    try {
      const subs = (catNew.subcategories || "").split(",").map(s => s.trim()).filter(Boolean);
      await axios.put(`${API}/admin/categories`, {
        key: catNew.key.toLowerCase().replace(/[^a-z0-9_]/g, ""),
        title: catNew.title,
        tag: catNew.tag,
        img: catNew.img,
        custom: true,
        ...(subs.length ? { subcategories: subs } : {}),
      }, { headers: headers() });
      setCatNew({ key: "", title: "", tag: "", img: "", subcategories: "" });
      setCatMsg("✓ Category added — now available in all listing & ad dropdowns");
      await loadCatalogue();
      await reloadCategoriesList();
    } catch (e) { setCatMsg(e.response?.data?.detail || "Add failed"); }
    finally { setCatBusy(false); }
  };

  const deleteCategoryOverride = async (key, custom) => {
    const msg = custom ? `Delete custom category "${key}"?` : `Reset "${key}" to its default image/title?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/admin/categories/${encodeURIComponent(key)}`, { headers: headers() });
      setCatMsg(custom ? "✓ Deleted" : "✓ Reset to default");
      await loadCatalogue();
      await reloadCategoriesList();
    } catch (e) { setCatMsg(e.response?.data?.detail || "Delete failed"); }
  };

  const loadAllListings = async () => {
    try {
      const r = await axios.get(`${API}/admin/businesses?q=${encodeURIComponent(listingFilter)}`, { headers: headers() });
      setAllListings(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Error loading listings"); }
  };

  const saveListingEdit = async (id, fields) => {
    try {
      await axios.put(`${API}/admin/businesses/${id}`, fields, { headers: headers() });
      setListingEditing(null);
      loadAllListings();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const deleteListing = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? Cascades popups, ads, leads.`)) return;
    try {
      await axios.delete(`${API}/admin/businesses/${id}`, { headers: headers() });
      loadAllListings();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const createNewBusinessAdmin = async () => {
    if (!newBizForm.name || !newBizForm.category) { alert("Name + category required"); return; }
    if (categories.length && !categories.some(c => c.key === newBizForm.category)) { alert(`"${newBizForm.category}" is not a valid category. Pick from the dropdown.`); return; }
    try {
      await axios.post(`${API}/admin/businesses/create`, newBizForm, { headers: headers() });
      setNewBizForm({ name: "", category: "", subcategory: "", desc: "", offer: "", img: "", address: "", phone: "", website: "" });
      setShowNewBiz(false);
      loadAllListings();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const loadEventsPending = async () => {
    try {
      const r = await axios.get(`${API}/admin/happening/pending`, { headers: headers() });
      setEventsPending(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Error"); }
  };

  const approvePublicEvent = async (id) => {
    try {
      await axios.post(`${API}/admin/happening/approve-public/${id}`, null, { headers: headers() });
      loadEventsPending();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const rejectPublicEvent = async (id) => {
    try {
      await axios.post(`${API}/admin/happening/reject-public/${id}`, null, { headers: headers() });
      loadEventsPending();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const loadPricing = async () => {
    try {
      const r = await axios.get(`${API}/admin/pricing`, { headers: headers() });
      setPricing(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Error"); }
  };

  const savePricing = async () => {
    setPricingMsg("");
    try {
      // Strip 'effective' (computed on backend); only send price + discount_pct
      const payload = {
        subscriptions: Object.fromEntries(
          Object.entries(pricing.subscriptions || {}).map(([k, v]) => [k, { price: v.price, discount_pct: v.discount_pct || 0 }])
        ),
        popup_ad:    { price: pricing.popup_ad.price,    discount_pct: pricing.popup_ad.discount_pct    || 0 },
        banner_ad:   { price: pricing.banner_ad.price,   discount_pct: pricing.banner_ad.discount_pct   || 0 },
        carousel_ad: { price: pricing.carousel_ad.price, discount_pct: pricing.carousel_ad.discount_pct || 0 },
      };
      await axios.put(`${API}/admin/pricing`, payload, { headers: headers() });
      setPricingMsg("✓ Saved — new prices apply on next checkout");
      loadPricing(); // refresh effective values
    } catch (e) { setPricingMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const loadPopups = async () => {
    try {
      const r = await axios.get(`${API}/admin/popup-ads`, { headers: headers() });
      setPopups(r.data || []);
    } catch (e) { setErr(e.response?.data?.detail || "Error loading popups"); }
  };

  const createAdminPopup = async () => {
    if (!newPopup.category || !newPopup.title.trim() || !newPopup.body.trim()) {
      setPopupMsg("⚠ Category, title, body required"); return;
    }
    setPopupBusy(true); setPopupMsg("");
    try {
      await axios.post(`${API}/admin/popup-ads/create`, {
        ...newPopup,
        business_id: (newPopup.business_id || "").trim() || null,
      }, { headers: headers() });
      setNewPopup({ category: "", title: "", body: "", image: "", cta_text: "Visit", cta_url: "", business_id: "" });
      setPopupMsg("✓ Pop-up live for 14 days — businesses can buy/get-gifted to claim ownership");
      loadPopups();
    } catch (e) { setPopupMsg("⚠ " + (e.response?.data?.detail || e.message || "Could not post pop-up")); }
    finally { setPopupBusy(false); }
  };

  const savePopupEdit = async () => {
    if (!popupEditing) return;
    setPopupModalErr("");
    if (!popupEditing.title?.trim() || !popupEditing.body?.trim()) { setPopupModalErr("Title and body required"); return; }
    setPopupBusy(true);
    try {
      await axios.put(`${API}/admin/popup-ads/by-id/${popupEditing.id}`, {
        category: popupEditing.category,
        title: popupEditing.title,
        body: popupEditing.body,
        image: popupEditing.image,
        cta_text: popupEditing.cta_text,
        cta_url: popupEditing.cta_url,
        business_id: (popupEditing.business_id || "").trim() || null,
      }, { headers: headers() });
      setPopupEditing(null);
      setPopupMsg("✓ Pop-up updated");
      loadPopups();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const status = e?.response?.status;
      if (status === 404 && /Not Found$/i.test(detail) && !detail.toLowerCase().includes("popup")) {
        // True endpoint-missing (FastAPI default 404 returns "Not Found")
        setPopupModalErr("Save endpoint not found on this server. Production may not have this build yet — try Force-Refresh below, or click Deploy in the Emergent platform.");
      } else if (status === 404) {
        // Endpoint exists but the popup id isn't in this database (e.g., stale UI list)
        setPopupModalErr("This pop-up no longer exists in the database. Close this dialog and reload — it may have been deleted by another admin.");
      } else {
        setPopupModalErr(typeof detail === "string" && detail ? detail : (e?.message || "Save failed"));
      }
      console.error("[popup save]", status, detail, e);
    }
    setPopupBusy(false);
  };

  const deletePopup = async (p) => {
    if (!window.confirm(`Delete pop-up "${p.title || p.category}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/popup-ads/by-id/${p.id}`, { headers: headers() });
      loadPopups();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const loadCarousel = async () => {
    try {
      const r = await axios.get(`${API}/admin/carousel-ads`, { headers: headers() });
      setCarousel(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Error"); }
  };

  const saveCarouselSeed = async () => {
    if (!carouselEditing?.headline?.trim() || !carouselEditing?.image_url?.trim()) {
      setCarouselMsg("Headline and image required"); return;
    }
    try {
      // If editing an existing ad (has id), use PUT by-id (works for paid + seed)
      if (carouselEditing.id) {
        await axios.put(`${API}/admin/carousel-ads/by-id/${carouselEditing.id}`, {
          headline: carouselEditing.headline,
          body: carouselEditing.body || "",
          image_url: carouselEditing.image_url,
          cta_text: carouselEditing.cta_text || "Visit",
          cta_url: carouselEditing.cta_url || "",
          business_id: carouselEditing.business_id || null,
        }, { headers: headers() });
      } else {
        // New seed
        await axios.post(`${API}/admin/carousel-ads/seed`, {
          slot: carouselEditing.slot,
          headline: carouselEditing.headline,
          body: carouselEditing.body || "",
          image_url: carouselEditing.image_url,
          cta_text: carouselEditing.cta_text || "Visit",
          cta_url: carouselEditing.cta_url || "",
          business_id: carouselEditing.business_id || null,
          duration_days: carouselEditing.duration_days || 14,
        }, { headers: headers() });
      }
      setCarouselEditing(null);
      setCarouselMsg("✓ Saved");
      loadCarousel();
    } catch (e) { setCarouselMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const deleteCarouselById = async (s) => {
    const label = s.status === "paid" ? "PAID carousel ad — owner has paid for this slot. Refund manually if needed." : "carousel ad";
    if (!window.confirm(`Delete ${label} "${s.headline || s.slot}"? Slot becomes open immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/carousel-ads/by-id/${s.id}`, { headers: headers() });
      loadCarousel();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  // ---- BANNER admin ----
  const loadBanners = async () => {
    try {
      const r = await axios.get(`${API}/admin/banner-ads`, { headers: headers() });
      setBanners(r.data || []);
    } catch (e) { setErr(e.response?.data?.detail || "Error"); }
  };

  const saveBannerSeed = async () => {
    if (!bannerEditing?.headline?.trim() || !bannerEditing?.image_url?.trim()) {
      setBannerMsg("Headline and image required"); return;
    }
    try {
      if (bannerEditing.id) {
        await axios.put(`${API}/admin/banner-ads/by-id/${bannerEditing.id}`, {
          headline: bannerEditing.headline, body: bannerEditing.body || "",
          image_url: bannerEditing.image_url, cta_text: bannerEditing.cta_text || "Visit",
          cta_url: bannerEditing.cta_url || "", business_id: bannerEditing.business_id || null,
        }, { headers: headers() });
      } else {
        await axios.post(`${API}/admin/banner-ads/seed`, {
          slot: bannerEditing.slot, headline: bannerEditing.headline, body: bannerEditing.body || "",
          image_url: bannerEditing.image_url, cta_text: bannerEditing.cta_text || "Visit",
          cta_url: bannerEditing.cta_url || "", business_id: bannerEditing.business_id || null,
          duration_days: bannerEditing.duration_days || 28,
        }, { headers: headers() });
      }
      setBannerEditing(null); setBannerMsg("✓ Saved"); loadBanners();
    } catch (e) { setBannerMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const deleteBannerById = async (s) => {
    const label = s.status === "paid" ? "PAID banner ad — owner has paid. Refund manually." : "banner ad";
    if (!window.confirm(`Delete ${label} for ${s.slot.replace("_"," ")}? Slot becomes open.`)) return;
    try {
      await axios.delete(`${API}/admin/banner-ads/by-id/${s.id}`, { headers: headers() });
      loadBanners();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  // ---- AD APPROVAL QUEUE ----
  const loadPendingAds = async () => {
    try {
      const r = await axios.get(`${API}/admin/ads/pending`, { headers: headers() });
      setPendingAds(r.data || []); setPendingMsg("");
    } catch (e) { setErr(e.response?.data?.detail || "Error"); }
  };
  const approvePendingAd = async (kind, id) => {
    try {
      await axios.post(`${API}/admin/ads/${kind}/${id}/approve`, {}, { headers: headers() });
      setPendingMsg("✓ Approved — now live");
      loadPendingAds();
    } catch (e) { setPendingMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };
  const rejectPendingAd = async (kind, id) => {
    const reason = window.prompt("Reason for rejection (optional, shown to business owner):", "") || "";
    try {
      await axios.post(`${API}/admin/ads/${kind}/${id}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers: headers() });
      setPendingMsg("✓ Rejected");
      loadPendingAds();
    } catch (e) { setPendingMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };
  const [happening, setHappening] = useState(null);
  const [hapImg, setHapImg] = useState("");
  const [hapDesc, setHapDesc] = useState("");
  const [hapWebsite, setHapWebsite] = useState("");
  const [hapBusy, setHapBusy] = useState(false);
  const [hapMsg, setHapMsg] = useState("");
  const [hapEditing, setHapEditing] = useState(null); // {id, image, description, website}
  const [hapEditMsg, setHapEditMsg] = useState("");

  const loadHappening = async () => {
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/happening`, { headers: headers() });
      setHappening(r.data);
      
    } catch (e) {
      setErr(e.response?.status === 401 ? "Invalid admin key" : "Error loading happening");
    }
  };

  const [hapBiz, setHapBiz] = useState("");  // business_id for new event

  const createHappening = async () => {
    if (!hapImg.trim() || !hapDesc.trim()) { setHapMsg("Image + description required"); return; }
    setHapBusy(true); setHapMsg("");
    try {
      await axios.post(`${API}/admin/happening`, { image: hapImg.trim(), description: hapDesc.trim(), website: hapWebsite.trim() || null, business_id: hapBiz.trim() || null }, { headers: headers() });
      setHapImg(""); setHapDesc(""); setHapWebsite(""); setHapBiz("");
      setHapMsg("✓ Posted — wipes Monday 2am PT");
      loadHappening();
    } catch (e) { setHapMsg("Error: " + (e.response?.data?.detail || e.message)); }
    setHapBusy(false);
  };

  const deleteHappening = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/happening/${id}`, { headers: headers() });
      loadHappening();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const saveHappeningEdit = async () => {
    if (!hapEditing) return;
    setHapEditMsg("");
    try {
      await axios.put(`${API}/admin/happening/${hapEditing.id}`, {
        image: hapEditing.image,
        description: hapEditing.description,
        website: hapEditing.website || null,
        business_id: hapEditing.business_id === undefined ? undefined : (hapEditing.business_id || ""),
      }, { headers: headers() });
      setHapEditing(null);
      loadHappening();
    } catch (e) { setHapEditMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const relistHappening = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/happening/${id}/relist`, null, { headers: headers() });
      loadHappening();
      alert(`Relisted — expires ${new Date(r.data.expires_at).toLocaleString()}`);
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  // ---- PHOTO LIBRARY admin ----
  // Browse every photo in durable storage, see where each one is used,
  // re-attach lost references, and detach stale ones — all without re-uploading.
  const [photos, setPhotos] = useState(null);       // {total, stored, missing, photos:[]}
  const [photoSearch, setPhotoSearch] = useState("");
  const [photoFilter, setPhotoFilter] = useState("all"); // all | stored | missing | orphan | used
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoMsg, setPhotoMsg] = useState("");
  const [photoAttachTarget, setPhotoAttachTarget] = useState(null); // {filename}

  const loadPhotos = async () => {
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/photos?limit=2000`, { headers: headers() });
      setPhotos(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Error loading photo library");
    }
  };

  const attachPhotoToTarget = async (filename, target_kind, target_id) => {
    setPhotoBusy(true); setPhotoMsg("");
    try {
      await axios.post(`${API}/admin/photos/attach`, {
        filename, target_kind, target_id,
      }, { headers: headers() });
      setPhotoMsg(`✓ Attached`);
      setPhotoAttachTarget(null);
      loadPhotos();
    } catch (e) { setPhotoMsg("⚠ " + (e.response?.data?.detail || e.message)); }
    finally { setPhotoBusy(false); }
  };

  // Back-compat wrapper used by older code paths.
  const attachPhotoToBusiness = (filename, business_id) => attachPhotoToTarget(filename, "business", business_id);

  const deletePhotoRefs = async (filename) => {
    if (!window.confirm(`Strip every catalog reference to ${filename}? The file itself remains in durable storage (you can re-attach later).`)) return;
    setPhotoBusy(true); setPhotoMsg("");
    try {
      const r = await axios.delete(`${API}/admin/photos/${filename}`, { headers: headers() });
      setPhotoMsg(`✓ Stripped ${r.data.references_stripped || 0} references`);
      loadPhotos();
    } catch (e) { setPhotoMsg("⚠ " + (e.response?.data?.detail || e.message)); }
    finally { setPhotoBusy(false); }
  };

  const hardDeletePhoto = async (filename) => {
    if (!window.confirm(`PERMANENTLY remove ${filename} from the library?\nThis strips every catalog reference and tombstones the file so it disappears from the library and /api/uploads/${filename} returns 404. This cannot be undone from the admin UI.`)) return;
    setPhotoBusy(true); setPhotoMsg("");
    try {
      const r = await axios.delete(`${API}/admin/photos/${filename}?purge=true`, { headers: headers() });
      setPhotoMsg(`✓ Purged (stripped ${r.data.references_stripped || 0} refs)`);
      loadPhotos();
    } catch (e) { setPhotoMsg("⚠ " + (e.response?.data?.detail || e.message)); }
    finally { setPhotoBusy(false); }
  };

  // ─── Casino Business subscriptions (v47 Phase 2 admin CRUD) ───────────
  const [casinoSubs, setCasinoSubs] = useState(null);
  const [casinoCreate, setCasinoCreate] = useState({ owner_user_id: "", company_name: "", business_limit: 5 });
  const [casinoBusy, setCasinoBusy] = useState(false);
  const [casinoMsg, setCasinoMsg] = useState("");
  const [attachSelect, setAttachSelect] = useState({}); // {sub_id: business_id}

  const loadCasinoSubs = async () => {
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/casino-subscriptions`, { headers: headers() });
      setCasinoSubs(r.data.subscriptions || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Error loading casino subscriptions");
      setCasinoSubs([]);
    }
  };
  const createCasinoSub = async () => {
    setCasinoBusy(true); setCasinoMsg("");
    try {
      await axios.post(`${API}/admin/casino-subscriptions`, casinoCreate, { headers: headers() });
      setCasinoMsg("✓ Casino subscription created");
      setCasinoCreate({ owner_user_id: "", company_name: "", business_limit: 5 });
      loadCasinoSubs();
    } catch (e) { setCasinoMsg("⚠ " + (e.response?.data?.detail || e.message)); }
    finally { setCasinoBusy(false); }
  };
  const patchCasinoSub = async (sub_id, patch) => {
    try {
      await axios.patch(`${API}/admin/casino-subscriptions/${sub_id}`, patch, { headers: headers() });
      loadCasinoSubs();
    } catch (e) { setCasinoMsg("⚠ " + (e.response?.data?.detail || e.message)); }
  };
  const attachBiz = async (sub_id) => {
    const business_id = attachSelect[sub_id];
    if (!business_id) return;
    try {
      await axios.post(`${API}/admin/casino-subscriptions/${sub_id}/attach-business?business_id=${encodeURIComponent(business_id)}`, {}, { headers: headers() });
      setAttachSelect(prev => ({ ...prev, [sub_id]: "" }));
      loadCasinoSubs();
    } catch (e) { setCasinoMsg("⚠ " + (e.response?.data?.detail || e.message)); }
  };
  const detachBiz = async (sub_id, business_id) => {
    if (!window.confirm(`Detach this business from the casino subscription?`)) return;
    try {
      await axios.post(`${API}/admin/casino-subscriptions/${sub_id}/detach-business?business_id=${encodeURIComponent(business_id)}`, {}, { headers: headers() });
      loadCasinoSubs();
    } catch (e) { setCasinoMsg("⚠ " + (e.response?.data?.detail || e.message)); }
  };

  const loadClaims = async () => {
    setErr("");
    try {
      const res = await axios.get(`${API}/admin/claims`, { headers: headers() });
      setClaims(res.data);
      
    } catch (e) {
      setErr(e.response?.status === 401 ? "Invalid admin key" : "Error loading");
      setClaims(null);
    }
  };

  const loadPending = async () => {
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/businesses/pending`, { headers: headers() });
      setPending(r.data);
      
    } catch (e) {
      setErr(e.response?.status === 401 ? "Invalid admin key" : "Error loading pending");
    }
  };

  const approvePending = async (bizId) => {
    try {
      await axios.post(`${API}/admin/businesses/${bizId}/approve`, null, { headers: headers() });
      loadPending();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const rejectPending = async (bizId) => {
    try {
      await axios.post(`${API}/admin/businesses/${bizId}/reject`, null, { headers: headers() });
      loadPending();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const loadBizs = async () => {
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/businesses`, { headers: headers(), params: bizSearch ? { q: bizSearch } : {} });
      setBizs(r.data);
      
    } catch (e) {
      setErr(e.response?.status === 401 ? "Invalid admin key" : "Error loading businesses");
    }
  };

  const approve = async (id) => {
    try {
      await axios.post(`${API}/admin/claims/${id}/approve`, form, { headers: headers() });
      setExpanded(null);
      loadClaims();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const reject = async (id) => {
    try {
      await axios.post(`${API}/admin/claims/${id}/reject`, null, { headers: headers() });
      loadClaims();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const toggleExpand = (c) => {
    if (expanded === c.id) { setExpanded(null); return; }
    setExpanded(c.id);
    setForm({ category: "whatsnew", subcategory: "", address: "Las Vegas, NV", offer: "NEW LISTING", img: "", desc: c.name ? `New listing: ${c.name}` : "" });
  };

  const [giftCasinoOwner, setGiftCasinoOwner] = useState("");
  const [giftCasinoEmail, setGiftCasinoEmail] = useState("");
  const [giftCasinoCompany, setGiftCasinoCompany] = useState("");

  const giftSub = async () => {
    setGiftMsg("");
    try {
      let body;
      if (subPlan === "casino") {
        const owner = giftCasinoOwner.trim();
        const email = giftCasinoEmail.trim().toLowerCase();
        if (!owner && !email) return setGiftMsg("Casino gift needs an email (or owner_user_id)");
        body = { plan: "casino", owner_user_id: owner, owner_email: email, company_name: giftCasinoCompany.trim(), months: parseInt(subMonths, 10), business_limit: 5 };
      } else {
        if (!selBiz) return setGiftMsg("Pick a business first");
        body = { business_id: selBiz.id, months: parseInt(subMonths, 10), plan: subPlan };
      }
      const r = await axios.post(`${API}/admin/gift/subscription`, body, { headers: headers() });
      const who = r.data.owner_email || r.data.owner_user_id || (selBiz && selBiz.name) || "recipient";
      setGiftMsg(`✓ ${subPlan.toUpperCase()} subscription gifted to ${who} ${r.data.lifetime ? "for life" : "until " + new Date(r.data.until).toLocaleDateString()}`);
      loadBizs();
    } catch (e) { setGiftMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const giftPopup = async () => {
    if (!selBiz) return setGiftMsg("Pick a business first");
    setGiftMsg("");
    try {
      const r = await axios.post(`${API}/admin/gift/popup-ad`, { business_id: selBiz.id, category: popupCat, weeks: parseInt(popupWeeks, 10) }, { headers: headers() });
      setGiftMsg(`✓ Pop-up ad gifted to ${selBiz.name} in ${popupCat} until ${new Date(r.data.expires_at).toLocaleDateString()}`);
    } catch (e) { setGiftMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const giftBanner = async () => {
    if (!selBiz) return setGiftMsg("Pick a business first");
    setGiftMsg("");
    try {
      const r = await axios.post(`${API}/admin/gift/banner-ad`, { business_id: selBiz.id, slot: bannerSlot, weeks: parseInt(bannerWeeks, 10) }, { headers: headers() });
      setGiftMsg(`✓ Banner ad gifted (${bannerSlot}) ${new Date(r.data.starts_at).toLocaleDateString()} → ${new Date(r.data.ends_at).toLocaleDateString()}`);
    } catch (e) { setGiftMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="min-h-screen bg-bg text-white grain">
      <LiveStatusIndicator />
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-bg/75 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/brand/zipcity-logo-v2.jpeg" alt="ZipCityLV" className="h-12 sm:h-20 w-auto zc-logo-glow" style={{ objectFit: "contain" }} />
          </Link>
          <div className="font-serif-accent text-gold text-[11px]">Admin Console</div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="font-serif-accent text-gold mb-2">~ Staff only</div>
        <h1 className="font-display uppercase text-4xl tracking-tight mb-8">Admin Console</h1>

        {!adminMe && !bootstrapMode && (
          <div data-testid="admin-login-card" className="max-w-md mx-auto border border-gold/40 rounded-sm p-7 bg-gradient-to-br from-gold/10 to-transparent">
            <div className="font-serif-accent text-gold text-sm">~ Sign in</div>
            <div className="font-display uppercase text-2xl tracking-tight mt-1">Admin login</div>
            <div className="text-muted text-xs mt-2">Use your assigned username and password.</div>

            <div className="mt-5 space-y-3">
              <input data-testid="admin-login-user" autoFocus value={loginUser} onChange={(e) => setLoginUser(e.target.value)} placeholder="Username" autoComplete="username" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
              <input data-testid="admin-login-pwd" type="password" value={loginPwd} onChange={(e) => setLoginPwd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLogin()} placeholder="Password" autoComplete="current-password" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
              {loginErr && <div data-testid="admin-login-err" className="text-red-400 text-xs">{loginErr}</div>}
              <button data-testid="admin-login-btn" onClick={doLogin} disabled={loginBusy} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">
                {loginBusy ? "Signing in…" : "Sign in"}
              </button>
              <button data-testid="bootstrap-toggle" type="button" onClick={() => { setBootstrapMode(true); setLoginErr(""); }} className="w-full text-[10px] text-muted hover:text-gold uppercase tracking-[0.2em] font-display py-2">First-time setup with master key →</button>
            </div>
          </div>
        )}

        {!adminMe && bootstrapMode && (
          <div data-testid="admin-bootstrap-card" className="max-w-md mx-auto border border-gold/40 rounded-sm p-7 bg-gradient-to-br from-gold/10 to-transparent">
            <div className="font-serif-accent text-gold text-sm">~ One-time setup</div>
            <div className="font-display uppercase text-2xl tracking-tight mt-1">Create first super-admin</div>
            <div className="text-muted text-xs mt-2 leading-relaxed">Paste the master ADMIN_KEY from your server .env, then pick a username + password. After this, log in normally with username/password — the master key becomes a recovery backdoor only.</div>

            <div className="mt-5 space-y-3">
              <input data-testid="bootstrap-master-key" type="password" value={bootstrapKey} onChange={(e) => setBootstrapKey(e.target.value)} placeholder="Master ADMIN_KEY" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
              <input data-testid="bootstrap-user" value={loginUser} onChange={(e) => setLoginUser(e.target.value)} placeholder="Choose username" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
              <input data-testid="bootstrap-pwd" type="password" value={loginPwd} onChange={(e) => setLoginPwd(e.target.value)} placeholder="Choose password (8+ chars)" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
              {loginErr && <div className="text-red-400 text-xs">{loginErr}</div>}
              <button data-testid="bootstrap-btn" onClick={doBootstrap} disabled={loginBusy} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">{loginBusy ? "Creating…" : "Create & sign in"}</button>
              <button type="button" onClick={() => { setBootstrapMode(false); setLoginErr(""); }} className="w-full text-[10px] text-muted hover:text-white uppercase tracking-[0.2em] font-display py-2">← Back to login</button>
            </div>
          </div>
        )}

        {adminMe && (
          <>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="text-muted text-xs">Signed in as <span className="text-gold font-display uppercase tracking-[0.2em]">{adminMe.username}</span> <span className="text-muted ml-2">· {adminMe.role}</span></div>
              <div className="flex gap-2">
                <button data-testid="admin-load-btn" onClick={() => { if (tab === "claims") loadClaims(); else if (tab === "affiliates") loadAffs(); else if (tab === "pending") loadPending(); else if (tab === "happening") loadHappening(); else if (tab === "photos") loadPhotos(); else loadBizs(); }} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110">Reload</button>
                <button data-testid="admin-logout-btn" onClick={doLogout} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-white/15 hover:border-red-400 hover:text-red-400 rounded-sm">Sign out</button>
              </div>
            </div>

            <div className="border-b border-white/5 flex mb-6 overflow-x-auto">
              {[{ id: "analytics", label: "Analytics" }, { id: "claims", label: "Claim Review" }, { id: "pending", label: "Pending Listings" }, { id: "all_listings", label: "All Listings" }, { id: "happening", label: "Happening" }, { id: "events_pending", label: "Pending Events" }, { id: "ad_queue", label: "Ad Approvals" }, { id: "ads247", label: "24/7 Ads" }, { id: "popups", label: "Pop-up Ads" }, { id: "carousel", label: "Carousel" }, { id: "banners", label: "Banners" }, { id: "photos", label: "Photos" }, { id: "emails", label: "Emails" }, { id: "categories", label: "Categories" }, { id: "casino_subs", label: "Casino Subs" }, { id: "gifts", label: "Gifts" }, { id: "affiliates", label: "Affiliates" }, { id: "pricing", label: "Pricing" }, { id: "settings", label: "Settings" }, ...((adminMe.role === "super" || adminMe.role === "master") ? [{ id: "team", label: "Team" }] : [])].map(t => (
                <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => { setTab(t.id); setErr(""); setGiftMsg(""); if (t.id === "claims") loadClaims(); if (t.id === "gifts") loadBizs(); if (t.id === "affiliates") loadAffs(); if (t.id === "pending") loadPending(); if (t.id === "happening") { loadHappening(); loadBizs(); } if (t.id === "team") loadAdminUsers(); if (t.id === "all_listings") loadAllListings(); if (t.id === "events_pending") loadEventsPending(); if (t.id === "pricing") loadPricing(); if (t.id === "carousel") loadCarousel(); if (t.id === "popups") loadPopups(); if (t.id === "banners") loadBanners(); if (t.id === "ad_queue") loadPendingAds(); if (t.id === "ads247") loadAds247(); if (t.id === "categories") loadCatalogue(); if (t.id === "analytics") loadAnalytics(); if (t.id === "settings") loadSettings(); if (t.id === "photos") { loadPhotos(); loadBizs(); } if (t.id === "emails") loadEmails(); if (t.id === "casino_subs") { loadCasinoSubs(); loadBizs(); } }} className={`px-5 py-3 font-display uppercase text-[11px] tracking-[0.2em] whitespace-nowrap ${tab === t.id ? "text-gold border-b-2 border-gold" : "text-muted hover:text-white"}`}>{t.label}</button>
              ))}
            </div>

            {err && (
              <div className="flex items-start justify-between gap-3 mb-5 p-3 rounded-sm border border-red-400/40 bg-red-400/5">
                <div className="text-red-300 font-serif-accent text-sm flex-1" data-testid="admin-err-banner">{err}</div>
                <button type="button" onClick={() => setErr("")} data-testid="admin-err-dismiss" className="text-red-300 hover:text-white text-xs font-display uppercase tracking-[0.2em] px-2 py-1 border border-red-300/30 rounded-sm">✕ Dismiss</button>
              </div>
            )}

        {tab === "claims" && claims !== null && (
          <div className="space-y-3">
            <div className="font-serif-accent text-muted">{claims.length} pending claim{claims.length === 1 ? "" : "s"}</div>
            {claims.length === 0 && <div className="text-muted py-10 text-center font-serif-accent">Inbox zero. ✨</div>}
            {claims.map(c => (
              <div key={c.id} data-testid={`claim-${c.id}`} className="border border-white/10 rounded-sm bg-surface/40">
                <div className="p-5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-display text-lg truncate">{c.name}</div>
                    <div className="text-muted text-xs font-serif-accent mt-1">user {c.userId} · {new Date(c.created_at).toLocaleString()}</div>
                    {c.email && <div className="text-muted text-xs">{c.email}</div>}
                    {c.license_number && <div className="text-gold text-xs font-display uppercase tracking-[0.18em] mt-1">License # {c.license_number}</div>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`expand-${c.id}`} onClick={() => toggleExpand(c)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold text-bg rounded-sm hover:brightness-110">
                      {expanded === c.id ? "Cancel" : "Approve…"}
                    </button>
                    <button data-testid={`reject-${c.id}`} onClick={() => reject(c.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-white/20 hover:border-red-400 hover:text-red-400 rounded-sm">Reject</button>
                  </div>
                </div>

                {expanded === c.id && (
                  <div data-testid={`approve-form-${c.id}`} className="border-t border-white/5 p-5 grid md:grid-cols-2 gap-3">
                    <select data-testid={`form-cat-${c.id}`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-bg border border-white/10 p-3 rounded-sm text-sm">
                      {categories.map(cat => <option key={cat.key} value={cat.key}>{cat.title}</option>)}
                    </select>
                    <SubcategoryPicker category={form.category} value={form.subcategory} onChange={(v) => setForm({ ...form, subcategory: v })} testId={`form-sub-${c.id}`} />
                    <input data-testid={`form-addr-${c.id}`} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />
                    <input data-testid={`form-offer-${c.id}`} value={form.offer} onChange={(e) => setForm({ ...form, offer: e.target.value })} placeholder="Offer tag" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    <input data-testid={`form-img-${c.id}`} value={form.img} onChange={(e) => setForm({ ...form, img: e.target.value })} placeholder="Image URL" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    <textarea data-testid={`form-desc-${c.id}`} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} rows={2} placeholder="Description" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />
                    <button data-testid={`approve-${c.id}`} onClick={() => approve(c.id)} className="md:col-span-2 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">
                      Approve & Create Listing
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "pending" && pending !== null && (
          <div className="space-y-3" data-testid="pending-list">
            <div className="font-serif-accent text-muted">{pending.length} listing{pending.length === 1 ? "" : "s"} awaiting approval</div>
            {pending.length === 0 && <div className="text-muted py-10 text-center font-serif-accent">All caught up — no pending listings.</div>}
            {pending.map(b => (
              <div key={b.id} data-testid={`pending-${b.id}`} className="border border-gold/30 rounded-sm p-5 flex items-start gap-4 bg-gradient-to-br from-gold/5 to-transparent">
                {b.img && <div className="w-28 h-24 rounded-sm bg-cover bg-center shrink-0" style={{ backgroundImage: `url('${b.img}')` }} />}
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase text-lg tracking-tight">{b.name}</div>
                  <div className="font-serif-accent text-gold text-sm">{b.category}{b.subcategory ? ` · ${b.subcategory}` : ""}</div>
                  <div className="text-muted text-xs mt-1">License: <span className="text-white">{b.license_number || "—"}</span></div>
                  <div className="text-muted text-xs mt-0.5">{b.address || "—"} · {b.phone || "—"}</div>
                  {b.website && <div className="text-muted text-xs mt-0.5 truncate"><a href={b.website} target="_blank" rel="noreferrer" className="text-gold hover:underline">{b.website}</a></div>}
                  <div className="text-muted text-xs mt-1 italic">{b.desc || "—"}</div>
                  <div className="text-[10px] text-muted/60 mt-1">Submitted {b.submitted_at?.slice(0,10)}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button data-testid={`approve-biz-${b.id}`} onClick={() => approvePending(b.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110">Approve</button>
                  <button data-testid={`reject-biz-${b.id}`} onClick={() => rejectPending(b.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-white/20 hover:border-red-400 hover:text-red-400 rounded-sm">Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "happening" && (
          <div className="space-y-6" data-testid="happening-admin">
            <div className="border border-gold/40 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent">
              <div className="font-serif-accent text-gold text-sm">~ Quick post</div>
              <div className="font-display uppercase text-lg tracking-tight">Add a happening</div>
              <div className="text-muted text-xs mt-1">Auto-expires next Monday at 2am Pacific. Subscribers get to post here free, but you can pre-fill while the inbox is quiet.</div>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-muted font-display mb-1.5">Image</label>
                  <ImageUploader value={hapImg} onChange={setHapImg} placeholder="Image URL or tap Upload →" />
                </div>
                <textarea data-testid="hap-desc" value={hapDesc} onChange={(e) => setHapDesc(e.target.value)} rows={3} placeholder="Description — what's happening, when, where" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input data-testid="hap-website" value={hapWebsite} onChange={(e) => setHapWebsite(e.target.value)} placeholder="Website (optional) · https://…" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <select data-testid="hap-biz" value={hapBiz} onChange={(e) => { if (!bizs.length) loadBizs(); setHapBiz(e.target.value); }} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm">
                  <option value="">— Link a business listing (optional) —</option>
                  {(bizs || []).map(b => <option key={b.id} value={b.id}>{b.name} · {b.category}</option>)}
                </select>
                <button data-testid="hap-post-btn" onClick={createHappening} disabled={hapBusy} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">
                  {hapBusy ? "Posting…" : "Post for this week"}
                </button>
                {hapMsg && <div className={`text-sm font-serif-accent ${hapMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{hapMsg}</div>}
              </div>
            </div>

            {happening !== null && (
              <div className="space-y-3">
                <div className="font-serif-accent text-muted">{happening.length} total entries · public list filters by expiration</div>
                {happening.length === 0 && <div className="text-muted py-10 text-center font-serif-accent">Nothing posted yet.</div>}
                {happening.map(ev => {
                  const expired = new Date(ev.expires_at) < new Date();
                  return (
                    <div key={ev.id} data-testid={`hap-row-${ev.id}`} className={`border rounded-sm p-4 flex items-start gap-4 ${expired ? "border-white/5 opacity-50" : "border-white/10 bg-bg/30"}`}>
                      {ev.image ? (
                        <div className="w-24 h-20 rounded-sm bg-cover bg-center shrink-0 border border-white/10" style={{ backgroundImage: `url('${ev.image}')` }} />
                      ) : (
                        <div className="w-24 h-20 rounded-sm shrink-0 border border-dashed border-amber-400/40 bg-amber-400/5 flex items-center justify-center text-amber-300 text-[9px] uppercase tracking-widest font-display text-center px-1">No image</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{ev.description}</div>
                        <div className="flex items-center gap-3 mt-1.5 text-[10px] uppercase tracking-[0.2em] text-muted font-display">
                          <span className={expired ? "text-red-400" : "text-gold"}>{expired ? "Expired" : "Live"}</span>
                          <span>· {ev.created_by}</span>
                          {ev.business_name && <span>· {ev.business_name}</span>}
                          <span>· wipes {new Date(ev.expires_at).toLocaleString()}</span>
                        </div>
                        {ev.website && <a href={ev.website} target="_blank" rel="noreferrer" className="text-gold text-xs truncate block mt-1 hover:underline">{ev.website}</a>}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <button data-testid={`hap-edit-${ev.id}`} onClick={() => { setHapEditMsg(""); if (!bizs.length) loadBizs(); setHapEditing({ id: ev.id, image: ev.image || "", description: ev.description || "", website: ev.website || "", business_id: ev.business_id || "", business_name: ev.business_name || "" }); }} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/20 hover:border-gold hover:text-gold rounded-sm">Edit</button>
                        {expired && (
                          <button data-testid={`hap-relist-${ev.id}`} onClick={() => relistHappening(ev.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-gold/40 text-gold hover:bg-gold hover:text-bg rounded-sm">Relist Next Week</button>
                        )}
                        <button data-testid={`hap-delete-${ev.id}`} onClick={() => deleteHappening(ev.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/20 hover:border-red-400 hover:text-red-400 rounded-sm">Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {hapEditing && (
              <div data-testid="hap-edit-modal" className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setHapEditing(null)}>
                <div onClick={(e) => e.stopPropagation()} className="bg-bg border border-gold/40 rounded-sm max-w-lg w-full p-7 my-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="font-serif-accent text-gold text-sm">~ Quick fix</div>
                      <div className="font-display uppercase text-2xl tracking-tight">Edit happening</div>
                      <div className="text-muted text-xs mt-1">Re-upload the image — it will be saved permanently with the entry.</div>
                    </div>
                    <button onClick={() => setHapEditing(null)} className="text-muted hover:text-white text-2xl leading-none">×</button>
                  </div>
                  <div className="space-y-3">
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-muted font-display">Image</label>
                    <ImageUploader value={hapEditing.image} onChange={(v) => setHapEditing({ ...hapEditing, image: v })} placeholder="Image URL or tap Upload →" />
                    <textarea data-testid="hap-edit-desc" value={hapEditing.description} onChange={(e) => setHapEditing({ ...hapEditing, description: e.target.value })} rows={3} placeholder="Description" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    <input data-testid="hap-edit-website" value={hapEditing.website} onChange={(e) => setHapEditing({ ...hapEditing, website: e.target.value })} placeholder="Website (optional)" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    <select data-testid="hap-edit-biz" value={hapEditing.business_id || ""} onChange={(e) => setHapEditing({ ...hapEditing, business_id: e.target.value })} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm">
                      <option value="">— No linked business (image + text only) —</option>
                      {(bizs || []).map(b => <option key={b.id} value={b.id}>{b.name} · {b.category}</option>)}
                    </select>
                    {hapEditing.business_name && !hapEditing.business_id && (
                      <div className="text-[10px] text-muted font-serif-accent">Currently un-linked · original submitter said: <span className="text-white">{hapEditing.business_name}</span></div>
                    )}
                    {hapEditMsg && <div className="p-3 border border-red-500/40 bg-red-500/10 rounded-sm text-red-300 text-sm">{hapEditMsg}</div>}
                    <div className="flex gap-2">
                      <button data-testid="hap-edit-save" onClick={saveHappeningEdit} className="flex-1 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">Save</button>
                      <button onClick={() => setHapEditing(null)} className="font-display uppercase tracking-[0.2em] text-xs py-3 px-6 border border-white/15 rounded-sm hover:border-red-400">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "gifts" && (
          <div className="space-y-6">
            <div className="border border-white/10 rounded-sm p-5 bg-surface/40">
              <div className="font-display uppercase tracking-tight text-lg mb-3">1. Find a Business</div>
              <div className="flex gap-2 mb-3">
                <input data-testid="gift-biz-search" value={bizSearch} onChange={(e) => setBizSearch(e.target.value)} placeholder="Search business name…" className="flex-1 bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <button data-testid="gift-biz-search-btn" onClick={loadBizs} className="font-display uppercase text-[11px] tracking-[0.2em] px-5 bg-gold text-bg rounded-sm">Search</button>
              </div>
              <div className="max-h-64 overflow-y-auto border border-white/5 rounded-sm">
                {bizs.length === 0 && <div className="text-muted text-center py-8 text-sm font-serif-accent">No results yet — load with the button above.</div>}
                {bizs.map(b => (
                  <button key={b.id} data-testid={`gift-biz-${b.id}`} onClick={() => setSelBiz(b)}
                    className={`w-full text-left p-3 border-b border-white/5 last:border-0 hover:bg-bg/40 transition-colors ${selBiz?.id === b.id ? "bg-gold/10" : ""}`}>
                    <div className="font-display text-sm truncate">{b.name}</div>
                    <div className="text-muted text-xs font-serif-accent">{b.category} · {b.subscription_status} {b.founding_member ? "· 👑 founding" : ""}</div>
                  </button>
                ))}
              </div>
              {selBiz && <div data-testid="gift-selected" className="mt-3 text-gold font-serif-accent text-sm">Selected: <span className="text-white">{selBiz.name}</span></div>}
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="border border-gold/30 rounded-sm p-5 bg-gradient-to-br from-gold/8 to-transparent">
                <div className="font-display uppercase tracking-tight">Gift Subscription</div>
                <div className="font-serif-accent text-gold text-xs mt-1">Free for X months — locks Founding Member status</div>
                <select data-testid="gift-sub-months" value={subMonths} onChange={(e) => setSubMonths(e.target.value)} className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm mt-3">
                  <option value="3">3 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                  <option value="999">Lifetime</option>
                </select>
                <select data-testid="gift-sub-plan" value={subPlan} onChange={(e) => setSubPlan(e.target.value)} className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm mt-2">
                  <option value="basic">Basic</option>
                  <option value="standard">Standard</option>
                  <option value="pro">Pro</option>
                  <option value="casino">Casino Business ($2,499)</option>
                </select>
                {subPlan === "casino" && (
                  <div className="mt-2 space-y-2 border border-purple-500/40 p-3 rounded-sm bg-purple-500/5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-display">Casino enterprise gift (owner-scoped, not per-business)</div>
                    <input data-testid="gift-casino-email" value={giftCasinoEmail} onChange={(e) => setGiftCasinoEmail(e.target.value)} placeholder="Recipient email (recommended) — auto-creates account" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-xs" />
                    <div className="text-[9px] text-muted/70 font-serif-accent">— OR —</div>
                    <input data-testid="gift-casino-owner" value={giftCasinoOwner} onChange={(e) => setGiftCasinoOwner(e.target.value)} placeholder="Owner user_id (advanced — leave blank if using email)" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-xs" />
                    <input data-testid="gift-casino-company" value={giftCasinoCompany} onChange={(e) => setGiftCasinoCompany(e.target.value)} placeholder="Company name (optional)" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-xs" />
                    <div className="text-[9px] text-muted/70 font-serif-accent">Recipient signs in via "For Businesses → Business" using this exact email.</div>
                  </div>
                )}
                <button data-testid="gift-sub-btn" onClick={giftSub} disabled={subPlan !== "casino" && !selBiz} className="w-full mt-3 font-display uppercase tracking-[0.2em] text-[11px] py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">Grant Subscription</button>
              </div>

              <div className="border border-white/10 rounded-sm p-5 bg-bg/40">
                <div className="font-display uppercase tracking-tight">Gift Pop-up Ad</div>
                <div className="font-serif-accent text-gold text-xs mt-1">Free category-takeover popup</div>
                <select data-testid="gift-popup-cat" value={popupCat} onChange={(e) => setPopupCat(e.target.value)} className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm mt-3">
                  {categories.map(c => <option key={c.key} value={c.key}>{c.title}</option>)}
                </select>
                <input data-testid="gift-popup-weeks" type="number" value={popupWeeks} onChange={(e) => setPopupWeeks(e.target.value)} min={1} max={52} placeholder="Weeks" className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm mt-2" />
                <button data-testid="gift-popup-btn" onClick={giftPopup} disabled={!selBiz} className="w-full mt-3 font-display uppercase tracking-[0.2em] text-[11px] py-3 border border-gold/40 hover:bg-gold hover:text-bg rounded-sm disabled:opacity-50">Grant Pop-up Ad</button>
              </div>

              <div className="border border-white/10 rounded-sm p-5 bg-bg/40">
                <div className="font-display uppercase tracking-tight">Gift Banner Ad</div>
                <div className="font-serif-accent text-gold text-xs mt-1">Free homepage banner — auto-queues</div>
                <select data-testid="gift-banner-slot" value={bannerSlot} onChange={(e) => setBannerSlot(e.target.value)} className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm mt-3">
                  {SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input data-testid="gift-banner-weeks" type="number" value={bannerWeeks} onChange={(e) => setBannerWeeks(e.target.value)} min={1} max={52} placeholder="Weeks" className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm mt-2" />
                <button data-testid="gift-banner-btn" onClick={giftBanner} disabled={!selBiz} className="w-full mt-3 font-display uppercase tracking-[0.2em] text-[11px] py-3 border border-gold/40 hover:bg-gold hover:text-bg rounded-sm disabled:opacity-50">Grant Banner Ad</button>
              </div>
            </div>

            {giftMsg && <div data-testid="gift-msg" className="text-gold font-serif-accent text-sm">{giftMsg}</div>}
          </div>
        )}

        {tab === "affiliates" && (
          <div className="space-y-4" data-testid="affiliates-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-serif-accent text-gold text-sm">~ 20% residual · 30-day free trial referrals</div>
                <div className="font-display uppercase text-2xl tracking-tight">{affs.length} {affs.length === 1 ? "affiliate" : "affiliates"}</div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Earned (lifetime)</div>
                  <div className="font-display text-xl gold-text">${affs.reduce((s, a) => s + (a.total_earned_dollars || 0), 0).toFixed(2)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Owed now</div>
                  <div className="font-display text-xl text-amber-300">${affs.reduce((s, a) => s + (a.balance_dollars || 0), 0).toFixed(2)}</div>
                </div>
                <button
                  data-testid="cleanup-test-affs-btn"
                  onClick={async () => {
                    if (!window.confirm("Remove seeded test affiliates? Real signups won't be touched.")) return;
                    try {
                      const r = await axios.post(`${API}/admin/affiliates/cleanup-tests`, null, { headers: headers() });
                      alert(`Removed ${r.data.removed_count} test affiliate${r.data.removed_count === 1 ? "" : "s"}`);
                      loadAffs();
                    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
                  }}
                  className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm"
                >× Clean tests</button>
              </div>
            </div>

            {/* Search */}
            <input
              data-testid="aff-search"
              value={affSearch}
              onChange={(e) => setAffSearch(e.target.value)}
              placeholder="Search by code, email, or name…"
              className="w-full bg-surface/60 border border-white/10 p-3 rounded-sm text-sm focus:border-gold/50"
            />

            {affs.length === 0 && (
              <div className="text-muted text-center py-12 font-serif-accent border border-white/5 rounded-sm bg-surface/30">
                No affiliates yet — share <a href="/affiliate" className="text-gold underline-offset-2 hover:underline">/affiliate</a> on socials to onboard your first.
              </div>
            )}

            {/* Card grid */}
            <div className="grid md:grid-cols-2 gap-3">
              {affs
                .filter(a => {
                  const q = (affSearch || "").trim().toLowerCase();
                  if (!q) return true;
                  return (a.code || "").toLowerCase().includes(q) || (a.email || "").toLowerCase().includes(q) || (a.name || "").toLowerCase().includes(q);
                })
                .map(a => {
                  const owed = a.balance_dollars > 0;
                  const hasPayoutInfo = !!a.payout_method;
                  const expanded = expandedAff === a.code;
                  return (
                    <div key={a.code} data-testid={`aff-card-${a.code}`} className={`border rounded-sm overflow-hidden transition-all ${owed ? "border-amber-400/40 bg-gradient-to-br from-amber-400/5 to-transparent" : "border-white/10 bg-bg/40"}`}>
                      <button
                        type="button"
                        data-testid={`aff-toggle-${a.code}`}
                        onClick={() => setExpandedAff(expanded ? null : a.code)}
                        className="w-full text-left p-4 hover:bg-white/5"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-display tracking-tight gold-text text-lg">{a.code}</div>
                            <div className="text-sm text-white truncate mt-0.5">{a.email || a.userId}</div>
                            {a.name && <div className="text-muted text-xs truncate">{a.name}</div>}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Balance</div>
                            <div className={`font-display text-xl ${owed ? "text-amber-300" : "text-white"}`}>${a.balance_dollars.toFixed(2)}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-muted font-display">Refs</div>
                            <div className="font-display text-sm">{a.referral_count}</div>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-muted font-display">Earned</div>
                            <div className="font-display text-sm gold-text">${a.total_earned_dollars.toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-muted font-display">Paid out</div>
                            <div className="font-display text-sm text-muted">${a.paid_out_dollars.toFixed(2)}</div>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {hasPayoutInfo ? (
                            <span className="font-display uppercase text-[9px] tracking-[0.22em] px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">✓ Payout info on file</span>
                          ) : (
                            <span className="font-display uppercase text-[9px] tracking-[0.22em] px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30">⚠ No payout info</span>
                          )}
                          {owed && <span className="font-display uppercase text-[9px] tracking-[0.22em] px-2 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/40">${a.balance_dollars.toFixed(2)} owed</span>}
                          <span className="ml-auto text-muted text-[10px] uppercase tracking-[0.18em] font-display">{expanded ? "▴ Hide" : "▾ View details"}</span>
                        </div>
                      </button>

                      {expanded && (
                        <div className="p-4 border-t border-white/10 bg-bg/40 space-y-4" data-testid={`aff-detail-${a.code}`}>
                          {/* Payout info block */}
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-display mb-2">Payout info from affiliate</div>
                            {hasPayoutInfo ? (
                              <div className="border border-gold/30 bg-gold/5 rounded-sm p-3 text-sm">
                                <div className="font-display uppercase text-[10px] tracking-[0.22em] text-gold">{a.payout_method}</div>
                                <div className="text-white mt-1 font-mono break-all" data-testid={`aff-payout-detail-${a.code}`}>
                                  {a.payout_method === "cashapp" && a.payout_details.cashtag}
                                  {a.payout_method === "venmo" && a.payout_details.username}
                                  {a.payout_method === "paypal" && a.payout_details.email}
                                  {a.payout_method === "zelle" && a.payout_details.handle}
                                  {a.payout_method === "bank" && (
                                    <span>{a.payout_details.holder_name} · {a.payout_details.account_type}<br />Routing {a.payout_details.routing_number}<br />Acct {a.payout_details.account_number}</span>
                                  )}
                                </div>
                                <div className="text-[10px] text-muted mt-2 font-serif-accent">Last updated {a.payout_info_updated_at?.slice(0, 10) || "—"}</div>
                                <button
                                  type="button"
                                  data-testid={`aff-copy-${a.code}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    let txt = "";
                                    if (a.payout_method === "cashapp") txt = a.payout_details.cashtag;
                                    else if (a.payout_method === "venmo") txt = a.payout_details.username;
                                    else if (a.payout_method === "paypal") txt = a.payout_details.email;
                                    else if (a.payout_method === "zelle") txt = a.payout_details.handle;
                                    else if (a.payout_method === "bank") txt = `${a.payout_details.holder_name} · ${a.payout_details.account_type} · Routing ${a.payout_details.routing_number} · Acct ${a.payout_details.account_number}`;
                                    navigator.clipboard?.writeText(txt || "");
                                    setCopiedAff(a.code);
                                    setTimeout(() => setCopiedAff(null), 1500);
                                  }}
                                  className="mt-2 font-display uppercase text-[9px] tracking-[0.22em] px-3 py-1.5 border border-gold/40 text-gold hover:bg-gold/10 rounded-sm"
                                >{copiedAff === a.code ? "✓ Copied" : "Copy to clipboard"}</button>
                              </div>
                            ) : (
                              <div className="border border-amber-400/30 bg-amber-400/5 text-amber-200 text-sm font-serif-accent rounded-sm p-3">
                                Affiliate hasn't saved payout info yet. Reach out to <span className="text-white">{a.email || a.userId}</span> first.
                              </div>
                            )}
                          </div>

                          {/* Mark-as-paid mini-form */}
                          {owed && (
                            <div data-testid={`aff-pay-form-${a.code}`}>
                              <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-display mb-2">Record manual payout</div>
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <div className="text-[9px] uppercase tracking-widest text-muted font-display mb-1">Amount $</div>
                                  <input
                                    data-testid={`payout-amount-${a.code}`}
                                    value={payoutFor === a.code ? payoutAmount : a.balance_dollars.toFixed(2)}
                                    onChange={(e) => { setPayoutFor(a.code); setPayoutAmount(e.target.value); }}
                                    onFocus={() => { setPayoutFor(a.code); setPayoutAmount(a.balance_dollars.toFixed(2)); }}
                                    className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm"
                                  />
                                </div>
                                <div>
                                  <div className="text-[9px] uppercase tracking-widest text-muted font-display mb-1">Method</div>
                                  <select data-testid={`payout-method-${a.code}`} value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)} className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm">
                                    <option value="paypal">PayPal</option>
                                    <option value="cashapp">Cash App</option>
                                    <option value="venmo">Venmo</option>
                                    <option value="zelle">Zelle</option>
                                    <option value="ach">ACH / Bank</option>
                                    <option value="wise">Wise</option>
                                    <option value="cash">Cash</option>
                                    <option value="other">Other</option>
                                  </select>
                                </div>
                                <div>
                                  <div className="text-[9px] uppercase tracking-widest text-muted font-display mb-1">&nbsp;</div>
                                  <button data-testid={`payout-confirm-${a.code}`} onClick={() => { if (payoutFor !== a.code) { setPayoutFor(a.code); setPayoutAmount(a.balance_dollars.toFixed(2)); } submitPayout(a); }} className="w-full font-display uppercase tracking-[0.2em] text-[10px] py-2.5 bg-gold-grad text-bg rounded-sm hover:brightness-110">Mark Paid</button>
                                </div>
                                <div className="col-span-3">
                                  <div className="text-[9px] uppercase tracking-widest text-muted font-display mb-1">Note (txn id, email…)</div>
                                  <input data-testid={`payout-note-${a.code}`} value={payoutNote} onChange={(e) => setPayoutNote(e.target.value)} className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" placeholder="optional" />
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Footer actions */}
                          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] font-display">
                            <span className="text-muted">Joined {a.created_at?.slice(0, 10) || "—"} · Last paid {a.last_payout_at?.slice(0, 10) || "—"}</span>
                            <button data-testid={`delete-aff-${a.code}`} onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Delete affiliate ${a.code} (${a.email || ""})? This removes the account but keeps existing payment records.`)) return;
                              try {
                                await axios.delete(`${API}/admin/affiliates/${a.code}`, { headers: headers() });
                                loadAffs();
                              } catch (er) { alert("Error: " + (er.response?.data?.detail || er.message)); }
                            }} className="text-red-400 hover:underline">× Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            <div className="text-muted text-xs font-serif-accent mt-4">
              Affiliates earn 20% of every paid invoice from any business that signed up via their `?ref=CODE` link. Their dashboard with QR + earnings is at <a className="text-gold" href="/affiliate">/affiliate</a>.
            </div>
          </div>
        )}

        {tab === "all_listings" && (
          <div className="space-y-4" data-testid="all-listings-tab">
            <div className="flex gap-2 flex-wrap">
              <input value={listingFilter} onChange={(e) => setListingFilter(e.target.value)} placeholder="Search by name…" className="flex-1 bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              <button data-testid="listings-load" onClick={loadAllListings} className="font-display uppercase text-[11px] tracking-[0.2em] px-5 bg-gold text-bg rounded-sm">Search</button>
              <button data-testid="new-biz-toggle" onClick={() => { setShowNewBiz(s => !s); if (!showNewBiz) reloadCategoriesList(); }} className="font-display uppercase text-[11px] tracking-[0.2em] px-5 bg-gold-grad text-bg rounded-sm hover:brightness-110">{showNewBiz ? "× Cancel" : "+ Add Listing"}</button>
            </div>

            {showNewBiz && (
              <div data-testid="new-biz-form" className="border border-gold/40 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent space-y-3">
                <div className="font-display uppercase text-lg tracking-tight">Seed a new business</div>
                <div className="text-muted text-xs">Goes live immediately, claimable by owner later.</div>
                <input value={newBizForm.name} onChange={(e) => setNewBizForm({ ...newBizForm, name: e.target.value })} placeholder="Name" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" data-testid="new-biz-name" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <select value={newBizForm.category} onChange={(e) => setNewBizForm({ ...newBizForm, category: e.target.value })} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" data-testid="new-biz-category" disabled={categoriesLoadState === "loading"}>
                      <option value="">{categoriesLoadState === "loading" ? "Loading categories…" : categoriesLoadState === "error" ? "Could not load — tap reload ↓" : "— Select a category —"}</option>
                      {categories.map(c => <option key={c.key} value={c.key}>{c.title}</option>)}
                    </select>
                    {categoriesLoadState === "error" && (
                      <button data-testid="new-biz-reload-cats" onClick={reloadCategoriesList} type="button" className="mt-1 text-[10px] uppercase tracking-[0.22em] text-gold font-display hover:underline">↻ Reload categories</button>
                    )}
                    {categoriesLoadState === "loaded" && categories.length === 0 && (
                      <div className="mt-1 text-[10px] text-red-300 font-serif-accent">No categories returned — try <button type="button" onClick={reloadCategoriesList} className="text-gold underline">reload</button></div>
                    )}
                  </div>
                  <SubcategoryPicker category={newBizForm.category} value={newBizForm.subcategory} onChange={(v) => setNewBizForm({ ...newBizForm, subcategory: v })} testId="new-biz-subcategory" />
                </div>
                <textarea value={newBizForm.desc} onChange={(e) => setNewBizForm({ ...newBizForm, desc: e.target.value })} rows={2} placeholder="Description" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input value={newBizForm.offer} onChange={(e) => setNewBizForm({ ...newBizForm, offer: e.target.value })} placeholder="Offer / Special" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <ImageUploader value={newBizForm.img} onChange={(v) => setNewBizForm({ ...newBizForm, img: v })} />
                <input value={newBizForm.address} onChange={(e) => setNewBizForm({ ...newBizForm, address: e.target.value })} placeholder="Address" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={newBizForm.phone} onChange={(e) => setNewBizForm({ ...newBizForm, phone: e.target.value })} placeholder="Phone" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                  <input value={newBizForm.website} onChange={(e) => setNewBizForm({ ...newBizForm, website: e.target.value })} placeholder="Website" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                </div>
                <button data-testid="new-biz-submit" onClick={createNewBusinessAdmin} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">Create listing</button>
              </div>
            )}

            {allListings && (
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="px-4 py-2 bg-bg/50 text-muted text-xs">{allListings.length} listings</div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {allListings.map(b => (
                    <div key={b.id} data-testid={`listing-${b.id}`} className="flex items-start gap-3 p-3 border-b border-white/5 hover:bg-bg/40">
                      <div className="w-16 h-14 shrink-0 rounded-sm bg-cover bg-center" style={{ backgroundImage: `url('${b.img}')` }} />
                      {listingEditing?.id === b.id ? (
                        <div className="flex-1 space-y-2">
                          <input defaultValue={b.name} onChange={(e) => listingEditing.name = e.target.value} placeholder="Name" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                          <div className="grid grid-cols-2 gap-2">
                            <select defaultValue={b.category} onChange={(e) => { listingEditing.category = e.target.value; setListingEditing({ ...listingEditing }); }} className="bg-bg border border-white/10 p-2 rounded-sm text-sm" data-testid={`edit-cat-${b.id}`}>
                              {categories.map(c => <option key={c.key} value={c.key}>{c.title}</option>)}
                            </select>
                            <SubcategoryPicker category={listingEditing.category ?? b.category} value={listingEditing.subcategory ?? b.subcategory ?? ""} onChange={(v) => { listingEditing.subcategory = v; setListingEditing({ ...listingEditing }); }} testId={`edit-sub-${b.id}`} />
                          </div>
                          <input defaultValue={b.offer} onChange={(e) => listingEditing.offer = e.target.value} placeholder="Offer" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                          <input defaultValue={b.address} onChange={(e) => listingEditing.address = e.target.value} placeholder="Address" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                          <input defaultValue={b.phone} onChange={(e) => listingEditing.phone = e.target.value} placeholder="Phone" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                          <input defaultValue={b.website} onChange={(e) => listingEditing.website = e.target.value} placeholder="Website" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                          <div className="grid grid-cols-2 gap-2">
                            <input data-testid={`edit-instagram-${b.id}`} defaultValue={b.instagram} onChange={(e) => listingEditing.instagram = e.target.value} placeholder="Instagram" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                            <input data-testid={`edit-tiktok-${b.id}`} defaultValue={b.tiktok} onChange={(e) => listingEditing.tiktok = e.target.value} placeholder="TikTok" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                            <input data-testid={`edit-facebook-${b.id}`} defaultValue={b.facebook} onChange={(e) => listingEditing.facebook = e.target.value} placeholder="Facebook" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                            <input data-testid={`edit-x-${b.id}`} defaultValue={b.x} onChange={(e) => listingEditing.x = e.target.value} placeholder="X (Twitter)" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                          </div>
                          {/* Multi-attach categories — admin-only, no $9.99 checkout needed */}
                          <div className="border border-white/10 rounded-sm p-3 bg-bg/40" data-testid={`edit-extra-cats-${b.id}`}>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display mb-2">Also list in (multi-select)</div>
                            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                              {categories
                                .filter(c => c.key !== (listingEditing.category ?? b.category))
                                .map(c => {
                                  const current = listingEditing.extra_categories ?? (b.extra_categories || []);
                                  const on = current.includes(c.key);
                                  return (
                                    <button
                                      key={c.key}
                                      type="button"
                                      data-testid={`edit-extra-cat-${b.id}-${c.key}`}
                                      onClick={() => {
                                        const next = on ? current.filter(x => x !== c.key) : [...current, c.key];
                                        listingEditing.extra_categories = next;
                                        setListingEditing({ ...listingEditing });
                                      }}
                                      className={`text-[10px] font-display uppercase tracking-[0.14em] px-2 py-1 rounded-full border ${on ? "bg-gold text-bg border-gold" : "border-white/10 text-muted hover:border-gold/40 hover:text-gold"}`}
                                    >
                                      {c.title}
                                    </button>
                                  );
                                })}
                            </div>
                            <div className="text-[9px] text-muted/70 mt-2 font-serif-accent">Tap any category to add/remove. Admin can attach a business to as many categories as needed — no $9.99/mo add-on required.</div>
                          </div>
                          <ImageUploader value={listingEditing.img ?? b.img} onChange={(v) => { listingEditing.img = v; setListingEditing({ ...listingEditing }); }} />
                          <textarea defaultValue={b.desc} onChange={(e) => listingEditing.desc = e.target.value} rows={2} placeholder="Description" className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                          <div className="flex gap-2">
                            <button data-testid={`save-${b.id}`} onClick={() => {
                              const patch = { name: listingEditing.name ?? b.name, category: listingEditing.category ?? b.category, subcategory: listingEditing.subcategory ?? b.subcategory, offer: listingEditing.offer ?? b.offer, address: listingEditing.address ?? b.address, phone: listingEditing.phone ?? b.phone, website: listingEditing.website ?? b.website, desc: listingEditing.desc ?? b.desc, instagram: listingEditing.instagram ?? b.instagram ?? "", tiktok: listingEditing.tiktok ?? b.tiktok ?? "", facebook: listingEditing.facebook ?? b.facebook ?? "", x: listingEditing.x ?? b.x ?? "" };
                              if (listingEditing.extra_categories !== undefined) patch.extra_categories = listingEditing.extra_categories;
                              // Only include img in the payload if the user actually changed it.
                              // Re-sending the existing base64 (often 500KB+) was triggering
                              // "Image too large" on Save. The backend keeps the prior value
                              // when img is omitted.
                              if (listingEditing.img !== undefined && listingEditing.img !== b.img) patch.img = listingEditing.img;
                              saveListingEdit(b.id, patch);
                            }} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm">Save</button>
                            <button onClick={() => setListingEditing(null)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-white/15 rounded-sm">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <div className="font-display text-sm truncate">{b.name}</div>
                            <div className="text-muted text-xs truncate">{b.category}{b.subcategory ? ` · ${b.subcategory}` : ""} · {b.address || "no address"}</div>
                            {b.offer && <div className="text-gold text-[11px] uppercase tracking-[0.18em] font-display mt-0.5 truncate">{b.offer}</div>}
                            <div className="flex items-center gap-3 mt-1 text-[10px] uppercase tracking-[0.18em] font-display text-muted">
                              <span data-testid={`listing-views-${b.id}`} className="text-gold">👁 {(b.views_total || 0).toLocaleString()} clicks</span>
                              {b.subscription_status && <span>· {b.subscription_status}</span>}
                              {b.founding_member && <span>· 👑 founding</span>}
                              {b.status === "pending_approval" && <span className="text-amber-400">· pending approval</span>}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1.5 shrink-0">
                            <button data-testid={`edit-${b.id}`} onClick={() => setListingEditing({ id: b.id })} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/15 hover:border-gold hover:text-gold rounded-sm">Edit</button>
                            <button data-testid={`pin-top-${b.id}`} onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/businesses/${b.id}/pin-to-top`, {}, { headers: headers() });
                                setErr(`✓ "${b.name}" pushed to top of ${b.category}`);
                                loadAllListings();
                              } catch (e) { setErr("⚠ " + (e.response?.data?.detail || e.message)); }
                            }} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-gold/50 text-gold hover:bg-gold hover:text-bg rounded-sm">↑ Push to Top</button>
                            <button data-testid={`delete-listing-${b.id}`} onClick={() => deleteListing(b.id, b.name)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">Delete</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "events_pending" && eventsPending !== null && (
          <div className="space-y-3" data-testid="events-pending-tab">
            <div className="font-serif-accent text-muted">{eventsPending.length} public event submission{eventsPending.length === 1 ? "" : "s"} awaiting approval</div>
            {eventsPending.length === 0 && <div className="text-muted py-10 text-center font-serif-accent">No pending submissions.</div>}
            {eventsPending.map(s => (
              <div key={s.id} data-testid={`event-pending-${s.id}`} className="border border-gold/30 rounded-sm p-4 flex items-start gap-4 bg-gradient-to-br from-gold/5 to-transparent">
                <div className="w-28 h-24 rounded-sm bg-cover bg-center shrink-0" style={{ backgroundImage: `url('${s.image}')` }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{s.description}</div>
                  <div className="text-muted text-xs mt-1">From: <span className="text-white">{s.submitter_email}</span>{s.submitter_name ? ` (${s.submitter_name})` : ""}</div>
                  {s.website && <div className="text-gold text-xs truncate"><a href={s.website} target="_blank" rel="noreferrer" className="hover:underline">{s.website}</a></div>}
                  <div className="text-muted text-[10px] mt-0.5">{s.submitted_at?.slice(0,16).replace("T"," ")}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button data-testid={`approve-event-${s.id}`} onClick={() => approvePublicEvent(s.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110">Approve</button>
                  <button data-testid={`reject-event-${s.id}`} onClick={() => rejectPublicEvent(s.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-white/20 hover:border-red-400 hover:text-red-400 rounded-sm">Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "popups" && (
          <div className="space-y-5" data-testid="popups-tab">
            <div className="font-serif-accent text-muted">One pop-up per category. Pre-fill them while the inbox is quiet — businesses can buy/be-gifted ownership later.</div>

            {popupMsg && <div className={`p-3 rounded-sm text-sm font-serif-accent ${popupMsg.startsWith("✓") ? "bg-gold/10 border border-gold/40 text-gold" : "bg-red-500/10 border border-red-500/40 text-red-300"}`}>{popupMsg}</div>}

            {/* Existing popups list */}
            {popups !== null && (
              <div className="space-y-3">
                <input
                  data-testid="popups-search"
                  value={popupSearch}
                  onChange={(e) => setPopupSearch(e.target.value)}
                  placeholder="🔍 Quick search — title, body, category, or business"
                  className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none"
                />
                {(() => {
                  const q = popupSearch.trim().toLowerCase();
                  const filteredPopups = q ? popups.filter(p => [p.title, p.body, p.category, p.business_name].some(x => (x || "").toLowerCase().includes(q))) : popups;
                  return (
                <>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">{filteredPopups.length} of {popups.length} pop-ups · grouped by category</div>
                {filteredPopups.length === 0 && <div className="text-muted py-10 text-center font-serif-accent border border-white/5 rounded-sm">{popups.length === 0 ? "No pop-ups yet — seed one below." : "No pop-ups match your search."}</div>}
                <div className="grid md:grid-cols-2 gap-3">
                  {filteredPopups.map((p, idx) => (
                    <div key={p.id || `popup-${idx}`} data-testid={`popup-row-${p.id || idx}`} className={`border rounded-sm p-4 ${p.is_admin_seed ? "border-white/10 bg-bg/30" : "border-gold/40 bg-gold/5"}`}>
                      <div className="flex items-start gap-3">
                        {p.image ? (
                          <div className="w-16 h-16 rounded-sm bg-cover bg-center shrink-0 border border-white/10" style={{ backgroundImage: `url('${p.image}')` }} />
                        ) : p.image_kind === "data_url_present" ? (
                          <div className="w-16 h-16 rounded-sm shrink-0 border border-gold/30 bg-gold/5 flex items-center justify-center text-gold text-[8px] uppercase tracking-widest font-display text-center px-1">Image set</div>
                        ) : (
                          <div className="w-16 h-16 rounded-sm shrink-0 border border-dashed border-amber-400/40 bg-amber-400/5 flex items-center justify-center text-amber-300 text-[8px] uppercase tracking-widest font-display text-center px-1">No img</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] uppercase tracking-widest font-display px-2 py-0.5 rounded-sm bg-bg/60 text-gold">{p.category}</span>
                            <span className={`text-[10px] uppercase tracking-widest font-display ${p.is_admin_seed ? "text-muted" : "text-gold"}`}>{p.is_admin_seed ? "seed" : "paid"}</span>
                          </div>
                          <div className="font-display text-sm truncate">{p.title || "—"}</div>
                          <div className="text-muted text-xs line-clamp-2">{p.body}</div>
                          {p.business_name_resolved && <div className="text-gold text-[10px] uppercase tracking-widest font-display mt-1">→ {p.business_name_resolved}</div>}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button data-testid={`popup-edit-${p.id}`} onClick={async () => {
                          setPopupMsg("");
                          // Fetch full popup (with heavy image) — list returns slim version
                          try {
                            const r = await axios.get(`${API}/admin/popup-ads/by-id/${p.id}`, { headers: headers() });
                            const full = r.data;
                            setPopupEditing({ id: full.id, category: full.category || "", title: full.title || "", body: full.body || "", image: full.image || "", cta_text: full.cta_text || "Visit", cta_url: full.cta_url || "", business_id: full.business_id || "" });
                          } catch (e) {
                            // fallback to slim version (image won't be editable until re-uploaded)
                            setPopupEditing({ id: p.id, category: p.category || "", title: p.title || "", body: p.body || "", image: "", cta_text: p.cta_text || "Visit", cta_url: p.cta_url || "", business_id: p.business_id || "" });
                          }
                        }} className="flex-1 font-display uppercase text-[10px] tracking-[0.2em] py-2 border border-white/15 hover:border-gold hover:text-gold rounded-sm">Edit</button>
                        <button data-testid={`popup-delete-${p.id}`} onClick={() => deletePopup(p)} className="flex-1 font-display uppercase text-[10px] tracking-[0.2em] py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
                </>
                );})()}
              </div>
            )}

            {/* Create new popup */}
            <div className="border border-gold/40 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent space-y-3">
              <div className="font-display uppercase text-lg tracking-tight">+ Add a pop-up ad</div>
              <div className="text-muted text-xs">Runs 14 days unless purchased/gifted. Replaces any existing default popup for that category.</div>

              <label className="block">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Category</span>
                  <button type="button" data-testid="popup-new-cats-reload" onClick={reloadCategoriesList} className="text-[10px] uppercase tracking-[0.22em] text-gold font-display hover:underline">↻ Reload</button>
                </div>
                <select data-testid="popup-new-category" value={newPopup.category} onChange={(e) => setNewPopup({ ...newPopup, category: e.target.value })} onFocus={reloadCategoriesList} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm">
                  <option value="">{categoriesLoadState === "loading" ? "Loading categories…" : categoriesLoadState === "error" ? "Could not load — tap ↻ Reload" : "— Select a category —"}</option>
                  {categories.map(c => <option key={c.key} value={c.key}>{c.title}</option>)}
                </select>
                {categoriesLoadState === "loaded" && categories.length > 0 && (
                  <div className="text-[10px] text-muted/70 font-serif-accent mt-1">{categories.length} categories available (includes admin-added custom)</div>
                )}
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">① TITLE — white headline (max 4 words)</span>
                <input data-testid="popup-new-title" value={newPopup.title} onChange={(e) => setNewPopup({ ...newPopup, title: e.target.value })} placeholder="e.g. HONEYS GENTLEMENS CLUB" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">② DESCRIPTION — body text under the headline</span>
                <textarea data-testid="popup-new-body" value={newPopup.body} onChange={(e) => setNewPopup({ ...newPopup, body: e.target.value })} rows={2} placeholder="e.g. Free VIP ride — we'll pick you up anywhere on the Strip" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">③ IMAGE — 1:1 square, fills the top of the ad</span>
                <ImageUploader value={newPopup.image} onChange={(v) => setNewPopup({ ...newPopup, image: v })} placeholder="Image URL or tap Upload →" />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">Link to active business (optional)</span>
                <BusinessPicker value={newPopup.business_id || ""} onChange={(v) => setNewPopup({ ...newPopup, business_id: v })} headers={headers()} testIdPrefix="popup-new-biz" />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">④ GOLD BAR TEXT (tagline)</span>
                  <input data-testid="popup-new-cta-text" value={newPopup.cta_text} onChange={(e) => setNewPopup({ ...newPopup, cta_text: e.target.value })} placeholder="e.g. FREE RIDE — CALL US" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">⑤ CTA URL (optional link)</span>
                  <input data-testid="popup-new-cta-url" value={newPopup.cta_url} onChange={(e) => setNewPopup({ ...newPopup, cta_url: e.target.value })} placeholder="https://..." className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                </label>
              </div>
              {popupMsg && (
                <div data-testid="popup-new-msg" className={`text-sm py-2 px-3 rounded-sm border ${popupMsg.startsWith("✓") ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5" : "text-red-300 border-red-500/30 bg-red-500/5"}`}>{popupMsg}</div>
              )}
              <button data-testid="popup-create-btn" onClick={createAdminPopup} disabled={popupBusy} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">{popupBusy ? "Posting…" : "Post pop-up"}</button>
            </div>

            {/* Edit modal */}
            {popupEditing && (
              <div data-testid="popup-edit-modal" className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => { if (!popupBusy) { setPopupEditing(null); setPopupModalErr(""); } }}>
                <div onClick={(e) => e.stopPropagation()} className="bg-bg border border-gold/40 rounded-sm max-w-lg w-full p-7 my-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="font-serif-accent text-gold text-sm">~ Edit</div>
                      <div className="font-display uppercase text-2xl tracking-tight">Edit pop-up ad</div>
                    </div>
                    <button onClick={() => { setPopupEditing(null); setPopupModalErr(""); }} className="text-muted hover:text-white text-2xl leading-none">×</button>
                  </div>
                  <div className="space-y-3">
                    <label className="block">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Category</span>
                        <button type="button" data-testid="popup-edit-cats-reload" onClick={reloadCategoriesList} className="text-[10px] uppercase tracking-[0.22em] text-gold font-display hover:underline">↻ Reload</button>
                      </div>
                      <select data-testid="popup-edit-category" value={popupEditing.category} onChange={(e) => setPopupEditing({ ...popupEditing, category: e.target.value })} onFocus={reloadCategoriesList} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm">
                        {categories.map(c => <option key={c.key} value={c.key}>{c.title}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">① TITLE — white headline (max 4 words)</span>
                      <input data-testid="popup-edit-title" value={popupEditing.title} onChange={(e) => setPopupEditing({ ...popupEditing, title: e.target.value })} placeholder="e.g. HONEYS GENTLEMENS CLUB" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">② DESCRIPTION — body text under headline</span>
                      <textarea data-testid="popup-edit-body" value={popupEditing.body} onChange={(e) => setPopupEditing({ ...popupEditing, body: e.target.value })} rows={2} placeholder="e.g. Free VIP ride — we'll pick you up anywhere on the Strip" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">③ IMAGE — 1:1 square, fills top of ad</span>
                      <ImageUploader value={popupEditing.image} onChange={(v) => setPopupEditing({ ...popupEditing, image: v })} placeholder="Image URL or tap Upload →" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">Link to active business (optional)</span>
                      <BusinessPicker value={popupEditing.business_id || ""} onChange={(v) => setPopupEditing({ ...popupEditing, business_id: v })} headers={headers()} testIdPrefix="popup-edit-biz" />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">④ GOLD BAR TEXT (tagline)</span>
                        <input data-testid="popup-edit-cta-text" value={popupEditing.cta_text} onChange={(e) => setPopupEditing({ ...popupEditing, cta_text: e.target.value })} placeholder="e.g. FREE RIDE — CALL US" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                      </label>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">⑤ CTA URL (optional link)</span>
                        <input data-testid="popup-edit-cta-url" value={popupEditing.cta_url} onChange={(e) => setPopupEditing({ ...popupEditing, cta_url: e.target.value })} placeholder="https://..." className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                      </label>
                    </div>
                    {popupModalErr && (
                      <div data-testid="popup-edit-error" className="p-3 border border-red-500/40 bg-red-500/10 rounded-sm text-red-300 text-sm font-serif-accent">
                        {popupModalErr}
                        {popupModalErr.includes("Force-Refresh") && (
                          <button type="button" onClick={() => {
                            try { Object.keys(localStorage).filter(k => k.startsWith("zc_")).forEach(k => { /* keep auth */ if (k !== "zc_admin_token" && k !== "zc_admin_key") localStorage.removeItem(k); }); } catch(_){ }
                            window.location.reload();
                          }} className="block mt-2 text-gold underline text-xs font-display uppercase tracking-[0.2em]">Force-refresh now →</button>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <button data-testid="popup-edit-save" onClick={savePopupEdit} disabled={popupBusy} className="flex-1 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">{popupBusy ? "Saving…" : "Save"}</button>
                      <button onClick={() => { setPopupEditing(null); setPopupModalErr(""); }} disabled={popupBusy} className="font-display uppercase tracking-[0.2em] text-xs py-3 px-6 border border-white/15 rounded-sm hover:border-red-400 disabled:opacity-50">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "carousel" && carousel !== null && (
          <div className="space-y-4" data-testid="carousel-tab">
            <div className="font-serif-accent text-muted">6 hero carousel slots — paid placements, auto-flip every 8s. Seed a slot until a business purchases it.</div>
            {carouselMsg && <div className={`text-sm font-serif-accent ${carouselMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{carouselMsg}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {carousel.map((s) => (
                <div key={s.slot} data-testid={`carousel-${s.slot}`} className={`border rounded-sm p-4 ${s.status === "paid" ? "border-gold bg-gold/5" : s.status === "seed" ? "border-white/20 bg-bg/40" : "border-dashed border-white/15"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display uppercase tracking-[0.2em] text-xs">{s.slot.replace("_", " ")}</div>
                    <div className={`text-[10px] uppercase tracking-widest font-display px-2 py-0.5 rounded-sm ${s.status === "paid" ? "bg-gold text-bg" : s.status === "seed" ? "bg-white/15" : "bg-bg/50 text-muted"}`}>{s.status}</div>
                  </div>
                  {s.status === "open" ? (
                    <div className="text-muted text-xs italic mb-3">Open — purchasable by any business</div>
                  ) : (
                    <>
                      {s.image_url && <div className="aspect-[16/9] bg-cover bg-center rounded-sm border border-white/10 mb-2" style={{ backgroundImage: `url('${s.image_url}')` }} />}
                      <div className="font-display text-sm">{s.headline}</div>
                      {s.body && <div className="text-muted text-xs mt-0.5">{s.body}</div>}
                      <div className="text-[10px] text-muted mt-1">CTA: {s.cta_text || "—"} {s.cta_url ? `→ ${s.cta_url}` : ""}</div>
                    </>
                  )}
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {s.status === "open" ? (
                      <button data-testid={`carousel-add-${s.slot}`} onClick={() => setCarouselEditing({ slot: s.slot, headline: "", body: "", image_url: "", cta_text: "Visit", cta_url: "", business_id: "", duration_days: 14 })} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110">+ Add ad</button>
                    ) : (
                      <>
                        <button data-testid={`carousel-edit-${s.slot}`} onClick={() => setCarouselEditing({ id: s.id, slot: s.slot, headline: s.headline || "", body: s.body || "", image_url: s.image_url || "", cta_text: s.cta_text || "Visit", cta_url: s.cta_url || "", business_id: s.business_id || "" })} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-white/15 hover:border-gold hover:text-gold rounded-sm">Edit</button>
                        <button data-testid={`carousel-delete-${s.slot}`} onClick={() => deleteCarouselById(s)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">Delete</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {carouselEditing && (
              <div data-testid="carousel-edit-form" className="border border-gold/40 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent space-y-3 mt-6">
                <div className="font-display uppercase text-lg tracking-tight">Seed slot — {carouselEditing.slot.replace("_", " ")}</div>
                <input value={carouselEditing.headline} onChange={(e) => setCarouselEditing({ ...carouselEditing, headline: e.target.value })} placeholder="Headline (e.g. Encore Beach Club)" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input value={carouselEditing.body} onChange={(e) => setCarouselEditing({ ...carouselEditing, body: e.target.value })} placeholder="Body / tagline (optional)" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <ImageUploader value={carouselEditing.image_url} onChange={(v) => setCarouselEditing({ ...carouselEditing, image_url: v })} aspectRatio="16/9" />

                {/* LIVE PREVIEW — exactly matches CarouselWheel rendering on homepage */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-display mb-2">↓ Live homepage preview</div>
                  <div className="relative rounded-sm overflow-hidden border border-gold/30 bg-bg/40" style={{ aspectRatio: "16/9" }} data-testid="admin-carousel-preview">
                    {carouselEditing.image_url ? (
                      <>
                        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${carouselEditing.image_url}')` }} />
                        <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/60 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 px-6 sm:px-10 py-5 sm:py-6 flex items-end justify-between gap-4">
                          <div className="max-w-[70%] sm:max-w-2xl min-w-0">
                            <div className="font-serif-accent text-gold text-[10px] sm:text-xs uppercase tracking-[0.3em]">Sponsored</div>
                            <div className="font-display uppercase text-lg sm:text-3xl tracking-tight mt-1 line-clamp-2">{carouselEditing.headline || "Your headline appears here"}</div>
                            {carouselEditing.body && <div className="text-white/80 text-xs sm:text-sm mt-1 sm:mt-2 line-clamp-2">{carouselEditing.body}</div>}
                          </div>
                          <div className="shrink-0 inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-gold-grad text-bg rounded-sm font-display uppercase text-[10px] sm:text-xs tracking-[0.2em] shadow-lg shadow-gold/20">
                            {carouselEditing.cta_text || "Visit"} →
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-muted text-sm font-serif-accent italic">Upload an image to see your live preview ↓</div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input value={carouselEditing.cta_text} onChange={(e) => setCarouselEditing({ ...carouselEditing, cta_text: e.target.value })} placeholder="CTA text" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                  <input value={carouselEditing.cta_url} onChange={(e) => setCarouselEditing({ ...carouselEditing, cta_url: e.target.value })} placeholder="CTA URL (optional)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                </div>
                <BusinessPicker value={carouselEditing.business_id || ""} onChange={(v) => setCarouselEditing({ ...carouselEditing, business_id: v })} headers={headers()} testIdPrefix="carousel-biz" />
                <input type="number" min="1" max="365" value={carouselEditing.duration_days || 14} onChange={(e) => setCarouselEditing({ ...carouselEditing, duration_days: parseInt(e.target.value, 10) || 14 })} placeholder="Duration days" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <div className="flex gap-2">
                  <button data-testid="carousel-save-btn" onClick={saveCarouselSeed} className="flex-1 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">Save seed</button>
                  <button onClick={() => setCarouselEditing(null)} className="font-display uppercase tracking-[0.2em] text-xs py-3 px-6 border border-white/15 rounded-sm hover:border-red-400">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "banners" && banners !== null && (
          <div className="space-y-6" data-testid="banners-tab">
            <div className="font-serif-accent text-muted">6 home-page banners · 6 spots each = <span className="text-gold">36 total slots</span>. Add a seed to fill any empty spot, edit a placeholder, or delete to free it back to the marketplace.</div>
            {bannerMsg && <div className={`text-sm font-serif-accent ${bannerMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{bannerMsg}</div>}

            {banners.map((slotGroup) => (
              <div key={slotGroup.slot} data-testid={`banner-group-${slotGroup.slot}`} className="border border-white/10 rounded-sm overflow-hidden">
                <div className="px-4 py-3 bg-bg/50 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    {slotMetaEdit?.slot === slotGroup.slot ? (
                      <div className="space-y-2" data-testid={`slot-meta-edit-${slotGroup.slot}`}>
                        <input data-testid={`slot-meta-label-${slotGroup.slot}`} value={slotMetaEdit.label} onChange={(e) => setSlotMetaEdit({ ...slotMetaEdit, label: e.target.value })} placeholder="Title (e.g. Hot Deals)" className="w-full bg-bg border border-gold/40 p-2 rounded-sm text-sm" />
                        <input data-testid={`slot-meta-tag-${slotGroup.slot}`} value={slotMetaEdit.tag} onChange={(e) => setSlotMetaEdit({ ...slotMetaEdit, tag: e.target.value })} placeholder="Tag (e.g. Tonight Only)" className="w-full bg-bg border border-white/15 p-2 rounded-sm text-xs" />
                        <input data-testid={`slot-meta-blurb-${slotGroup.slot}`} value={slotMetaEdit.blurb} onChange={(e) => setSlotMetaEdit({ ...slotMetaEdit, blurb: e.target.value })} placeholder="Blurb (one-line description)" className="w-full bg-bg border border-white/15 p-2 rounded-sm text-xs" />
                        <div className="flex gap-2">
                          <button data-testid={`slot-meta-save-${slotGroup.slot}`} onClick={saveSlotMeta} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 bg-gold-grad text-bg rounded-sm">Save title</button>
                          <button onClick={() => setSlotMetaEdit(null)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/15 rounded-sm">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="font-display uppercase text-base tracking-tight">{slotGroup.meta?.label || slotGroup.slot.replace("_", " ")}</div>
                          <button data-testid={`slot-meta-edit-btn-${slotGroup.slot}`} onClick={() => setSlotMetaEdit({ slot: slotGroup.slot, label: slotGroup.meta?.label || "", tag: slotGroup.meta?.tag || "", blurb: slotGroup.meta?.blurb || "" })} className="text-muted hover:text-gold text-xs font-display uppercase tracking-widest px-2 py-0.5 border border-white/10 rounded-sm">✎ Edit title</button>
                        </div>
                        <div className="font-serif-accent italic text-muted text-xs">{slotGroup.meta?.tag} · {slotGroup.meta?.blurb}</div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-display">
                    <span className={`px-2 py-1 rounded-sm ${slotGroup.filled === 6 ? "bg-gold/20 text-gold" : "bg-white/10 text-white"}`}>{slotGroup.filled}/6 filled</span>
                    {slotGroup.queued_count > 0 && <span className="px-2 py-1 rounded-sm bg-amber-400/20 text-amber-300">{slotGroup.queued_count} queued</span>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                  {slotGroup.spots.map((s) => (
                    <div key={`${slotGroup.slot}-${s.position}`} data-testid={`banner-spot-${slotGroup.slot}-${s.position}`}
                      className={`border rounded-sm p-3 ${s.status === "paid" ? "border-gold bg-gold/5" : s.status === "seed" ? "border-white/20 bg-bg/40" : "border-dashed border-white/15 bg-bg/20"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-widest font-display text-muted">Spot {s.position}</div>
                        <div className={`text-[10px] uppercase tracking-widest font-display px-2 py-0.5 rounded-sm ${s.status === "paid" ? "bg-gold text-bg" : s.status === "seed" ? "bg-white/15" : "bg-bg/50 text-muted"}`}>{s.status}</div>
                      </div>
                      {s.status === "open" ? (
                        <div className="text-muted text-xs italic mb-3 py-4 text-center">Empty — admin can seed or wait for a paid claim</div>
                      ) : (
                        <>
                          {s.image_url && <div className="aspect-square bg-cover bg-center rounded-sm border border-white/10 mb-2" style={{ backgroundImage: `url('${s.image_url}')` }} />}
                          <div className="font-display text-sm truncate">{s.headline || <span className="text-muted italic">No headline</span>}</div>
                          {s.body && <div className="text-muted text-xs mt-0.5 line-clamp-2">{s.body}</div>}
                          <div className="text-[10px] text-muted mt-1 truncate">CTA: {s.cta_text || "—"}</div>
                        </>
                      )}
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {s.status === "open" ? (
                          <button data-testid={`banner-add-${slotGroup.slot}-${s.position}`} onClick={() => setBannerEditing({ slot: slotGroup.slot, headline: "", body: "", image_url: "", cta_text: "Visit", cta_url: "", business_id: "", duration_days: 28 })} className="w-full font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110">+ Seed this spot</button>
                        ) : (
                          <>
                            <button data-testid={`banner-edit-${slotGroup.slot}-${s.position}`} onClick={() => setBannerEditing({ id: s.id, slot: slotGroup.slot, headline: s.headline || "", body: s.body || "", image_url: s.image_url || "", cta_text: s.cta_text || "Visit", cta_url: s.cta_url || "", business_id: s.business_id || "" })} className="flex-1 font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-white/15 hover:border-gold hover:text-gold rounded-sm">Edit</button>
                            <button data-testid={`banner-delete-${slotGroup.slot}-${s.position}`} onClick={() => deleteBannerById(s)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">×</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {bannerEditing && (
              <div data-testid="banner-edit-form" className="border border-gold/40 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent space-y-3 mt-6">
                <div className="font-display uppercase text-lg tracking-tight">{bannerEditing.id ? "Edit" : "Seed"} banner — {bannerEditing.slot.replace("_", " ")}</div>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">① HEADLINE — bold uppercase title</span>
                  <input value={bannerEditing.headline} onChange={(e) => setBannerEditing({ ...bannerEditing, headline: e.target.value })} placeholder="e.g. RESORTS WORLD" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">② BODY — small tagline under the headline</span>
                  <input value={bannerEditing.body} onChange={(e) => setBannerEditing({ ...bannerEditing, body: e.target.value })} placeholder="e.g. Pool club season — book cabanas now" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">③ IMAGE — 1:1 square, fills the banner</span>
                  <ImageUploader value={bannerEditing.image_url} onChange={(v) => setBannerEditing({ ...bannerEditing, image_url: v })} />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">④ CTA TEXT</span>
                    <input value={bannerEditing.cta_text} onChange={(e) => setBannerEditing({ ...bannerEditing, cta_text: e.target.value })} placeholder="e.g. Visit" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">⑤ CTA URL (optional)</span>
                    <input value={bannerEditing.cta_url} onChange={(e) => setBannerEditing({ ...bannerEditing, cta_url: e.target.value })} placeholder="https://..." className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">Link to active business (optional)</span>
                  <BusinessPicker value={bannerEditing.business_id || ""} onChange={(v) => setBannerEditing({ ...bannerEditing, business_id: v })} headers={headers()} testIdPrefix="banner-biz" />
                </label>
                {!bannerEditing.id && (
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">Duration (days)</span>
                    <input type="number" min="1" max="365" value={bannerEditing.duration_days || 28} onChange={(e) => setBannerEditing({ ...bannerEditing, duration_days: parseInt(e.target.value, 10) || 28 })} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                  </label>
                )}
                <div className="flex gap-2">
                  <button data-testid="banner-save-btn" onClick={saveBannerSeed} className="flex-1 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">Save</button>
                  <button onClick={() => setBannerEditing(null)} className="font-display uppercase tracking-[0.2em] text-xs py-3 px-6 border border-white/15 rounded-sm hover:border-red-400">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "ad_queue" && pendingAds !== null && (          <div className="space-y-4" data-testid="ad-queue-tab">
            <div className="font-serif-accent text-muted">Paid ads waiting for admin approval. Approve to make live; reject to refund manually.</div>
            {pendingMsg && <div className={`text-sm font-serif-accent ${pendingMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{pendingMsg}</div>}
            {pendingAds.length === 0 ? (
              <div className="text-muted py-12 text-center font-serif-accent border border-white/5 rounded-sm">No ads waiting for approval.</div>
            ) : (
              <div className="space-y-3">
                {pendingAds.map(p => {
                  const startsLater = p.starts_at && new Date(p.starts_at) > new Date();
                  return (
                    <div key={`${p.kind}-${p.id}`} data-testid={`pending-ad-${p.kind}-${p.id}`} className="border border-amber-400/30 bg-amber-400/5 rounded-sm p-4 flex items-start gap-3">
                      {p.image_url || p.image ? (
                        <div className="w-20 h-20 shrink-0 rounded-sm bg-cover bg-center border border-white/10" style={{ backgroundImage: `url('${p.image_url || p.image}')` }} />
                      ) : (
                        <div className="w-20 h-20 shrink-0 rounded-sm border border-dashed border-amber-400/40 bg-bg/40 flex items-center justify-center text-amber-300 text-[8px] uppercase tracking-widest font-display text-center px-1">No img</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest font-display px-2 py-0.5 rounded-sm bg-amber-400/20 text-amber-300">{p.kind}</span>
                          {p.slot && <span className="text-[10px] uppercase tracking-widest font-display text-muted">{p.slot.replace("_"," ")}</span>}
                          {p.category && <span className="text-[10px] uppercase tracking-widest font-display text-muted">{p.category}</span>}
                          {startsLater && <span className="text-[10px] uppercase tracking-widest font-display text-gold">Queued · starts {new Date(p.starts_at).toLocaleDateString()}</span>}
                        </div>
                        <div className="font-display text-sm truncate">{p.headline || p.title || "—"}</div>
                        <div className="text-muted text-xs line-clamp-2">{p.body || ""}</div>
                        {p.business_name_resolved && <div className="text-gold text-[10px] uppercase tracking-widest font-display mt-1">→ {p.business_name_resolved}</div>}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <button data-testid={`approve-${p.kind}-${p.id}`} onClick={() => approvePendingAd(p.kind, p.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 bg-gold-grad text-bg rounded-sm hover:brightness-110">Approve</button>
                        <button data-testid={`reject-${p.kind}-${p.id}`} onClick={() => rejectPendingAd(p.kind, p.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">Reject</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "ads247" && (
          <div className="space-y-4" data-testid="ads247-tab">
            <div className="font-serif-accent text-muted">Every business's 24/7 spotlight (db.ads). Admin can create new ads, edit content, or take down ads that violate policy / law. Owners are notified by email when their ad is edited.</div>

            {/* + Add a 24/7 Ad */}
            <div className="border border-gold/20 rounded-sm">
              <button data-testid="ads247-new-toggle" onClick={() => { setShowNewAd247(s => !s); setNewAd247Msg(""); }} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gold/5">
                <span className="font-display uppercase tracking-[0.2em] text-sm text-gold">+ Add a 24/7 Ad</span>
                <span className="text-muted text-xs">{showNewAd247 ? "▴ Close" : "▾ Open"}</span>
              </button>
              {showNewAd247 && (
                <div className="p-4 space-y-3 border-t border-white/5" data-testid="ads247-new-form">
                  <div className="text-muted text-xs font-serif-accent">Manually create a 24/7 spotlight ad for an existing business (no checkout required). Useful for featured promos, gifted slots, and seeded content.</div>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">① Link to active business *</span>
                    <BusinessPicker value={newAd247.business_id} onChange={(v) => setNewAd247({ ...newAd247, business_id: v })} headers={headers()} testIdPrefix="ads247-new-biz" />
                  </label>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">② Category *</span>
                    <select data-testid="ads247-new-category" value={newAd247.category} onChange={(e) => setNewAd247({ ...newAd247, category: e.target.value })} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm">
                      <option value="">— Select a category —</option>
                      {categories.map(c => <option key={c.key} value={c.key}>{c.title}</option>)}
                    </select>
                  </label>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">③ Headline *</span>
                    <input data-testid="ads247-new-headline" value={newAd247.headline} onChange={(e) => setNewAd247({ ...newAd247, headline: e.target.value })} placeholder="e.g. SAVE 25% TONIGHT ONLY" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                  </label>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">④ Body</span>
                    <textarea data-testid="ads247-new-body" value={newAd247.body} onChange={(e) => setNewAd247({ ...newAd247, body: e.target.value })} rows={2} placeholder="Short paragraph describing the offer" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                  </label>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">⑤ Image — 1:1 square</span>
                    <ImageUploader value={newAd247.image_url} onChange={(v) => setNewAd247({ ...newAd247, image_url: v })} />
                  </label>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">CTA Text</span>
                        <div className="flex gap-1" data-testid="ads247-cta-presets">
                          <button type="button" onClick={() => setNewAd247({ ...newAd247, cta_text: "Visit" })} className={`text-[9px] uppercase tracking-[0.18em] font-display px-2 py-0.5 rounded-sm border ${newAd247.cta_text === "Visit" ? "bg-gold text-bg border-gold" : "border-white/15 text-muted hover:border-gold/40 hover:text-gold"}`}>Visit</button>
                          <button type="button" onClick={() => setNewAd247({ ...newAd247, cta_text: "Call" })} className={`text-[9px] uppercase tracking-[0.18em] font-display px-2 py-0.5 rounded-sm border ${newAd247.cta_text === "Call" ? "bg-gold text-bg border-gold" : "border-white/15 text-muted hover:border-gold/40 hover:text-gold"}`}>Call</button>
                        </div>
                      </div>
                      <input data-testid="ads247-new-cta-text" value={newAd247.cta_text} onChange={(e) => setNewAd247({ ...newAd247, cta_text: e.target.value })} placeholder="Visit" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                      <div className="text-[9px] text-muted/70 font-serif-accent mt-1">Quick-pick: tap "Visit" (websites) or "Call" (phone). Free-text override allowed.</div>
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">CTA URL</span>
                      <input data-testid="ads247-new-cta-url" value={newAd247.cta_url} onChange={(e) => setNewAd247({ ...newAd247, cta_url: e.target.value })} placeholder="https://..." className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">Phone (tap-to-call button)</span>
                    <input data-testid="ads247-new-phone" value={newAd247.phone} onChange={(e) => setNewAd247({ ...newAd247, phone: e.target.value })} placeholder="702-555-0100" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                  </label>

                  {newAd247Msg && (
                    <div className={`text-sm py-2 px-3 rounded-sm border ${newAd247Msg.startsWith("✓") ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5" : "text-red-300 border-red-500/30 bg-red-500/5"}`} data-testid="ads247-new-msg">{newAd247Msg}</div>
                  )}
                  <button data-testid="ads247-create-btn" onClick={createAdmin247Ad} disabled={newAd247Busy} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">{newAd247Busy ? "Creating…" : "Create 24/7 Ad"}</button>
                </div>
              )}
            </div>

            <input
              data-testid="ads247-search"
              value={ads247Search}
              onChange={(e) => setAds247Search(e.target.value)}
              placeholder="🔍 Quick search — business name, headline, category, or phone"
              className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none"
            />
            {ads247Msg && <div className={`text-sm font-serif-accent ${ads247Msg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{ads247Msg}</div>}
            {ads247 === null ? (
              <div className="text-muted py-6 text-center font-serif-accent">Loading…</div>
            ) : ads247.length === 0 ? (
              <div className="text-muted py-12 text-center font-serif-accent">No 24/7 ads have been published yet.</div>
            ) : (() => {
              const q = ads247Search.trim().toLowerCase();
              const filtered = q ? ads247.filter(a => [a.business_name, a.headline, a.body, a.category, a.phone].some(x => (x || "").toLowerCase().includes(q))) : ads247;
              return (
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="px-4 py-2 bg-bg/50 text-muted text-xs">{filtered.length} of {ads247.length} ads · sorted by most recently edited</div>
                <div className="max-h-[75vh] overflow-y-auto">
                  {filtered.map((a) => (
                    <div key={a.id || `${a.business_id}-${a.category}`} data-testid={`ads247-row-${a.id}`} className="p-4 border-b border-white/5 hover:bg-bg/30">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-base truncate">{a.business_name || "(orphaned)"} <span className="text-muted text-xs">· {a.category}</span></div>
                          <div className="text-gold text-sm mt-1 truncate">{a.headline || <span className="text-muted italic">No headline</span>}</div>
                          <div className="text-muted text-xs mt-1 line-clamp-2">{a.body}</div>
                          <div className="text-[10px] text-muted font-display uppercase tracking-widest mt-2">
                            {a.active === false ? "● paused" : "● active"} · {a.phone ? `☏ ${a.phone}` : "no phone"} · updated {new Date(a.updated_at || Date.now()).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                          <button data-testid={`ads247-edit-${a.id}`} onClick={() => openAds247Edit(a.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/15 hover:border-gold hover:text-gold rounded-sm">Edit</button>
                          <button data-testid={`ads247-delete-${a.id}`} onClick={() => deleteAds247(a.id, a.business_name)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/15 hover:border-red-400 hover:text-red-400 rounded-sm">Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              );
            })()}
            {ads247Editing && (
              <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl" onClick={() => setAds247Editing(null)}>
                <div data-testid="ads247-edit-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-surface border border-gold/40 rounded-sm p-6 max-h-[90vh] overflow-y-auto">
                  <div className="font-display uppercase text-xl tracking-tight mb-1">Edit 24/7 Ad</div>
                  <div className="text-muted text-xs font-serif-accent mb-4">{ads247Editing.business_name} · {ads247Editing.category}</div>
                  <div className="space-y-3">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">Headline</span>
                      <input value={ads247Editing.headline || ""} onChange={(e) => setAds247Editing({ ...ads247Editing, headline: e.target.value })} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">Body</span>
                      <textarea value={ads247Editing.body || ""} onChange={(e) => setAds247Editing({ ...ads247Editing, body: e.target.value })} rows={3} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-display block mb-1.5">Image</span>
                      <ImageUploader value={ads247Editing.image_url || ""} onChange={(v) => setAds247Editing({ ...ads247Editing, image_url: v })} />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">CTA Text</span>
                        <input value={ads247Editing.cta_text || ""} onChange={(e) => setAds247Editing({ ...ads247Editing, cta_text: e.target.value })} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                      </label>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">CTA URL</span>
                        <input value={ads247Editing.cta_url || ""} onChange={(e) => setAds247Editing({ ...ads247Editing, cta_url: e.target.value })} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                      </label>
                    </div>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-muted font-display block mb-1.5">Phone (optional)</span>
                      <input value={ads247Editing.phone || ""} onChange={(e) => setAds247Editing({ ...ads247Editing, phone: e.target.value })} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={ads247Editing.active !== false} onChange={(e) => setAds247Editing({ ...ads247Editing, active: e.target.checked })} />
                      <span>Active (live on the business detail page)</span>
                    </label>
                  </div>
                  <div className="flex gap-2 mt-5">
                    <button data-testid="ads247-save-btn" onClick={saveAds247Edit} className="flex-1 font-display uppercase text-xs tracking-[0.2em] py-3 bg-gold-grad text-bg rounded-sm">Save</button>
                    <button onClick={() => setAds247Editing(null)} className="font-display uppercase text-xs tracking-[0.2em] py-3 px-4 border border-white/15 rounded-sm">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {tab === "photos" && (
          <div className="space-y-4" data-testid="photos-tab">
            <div className="font-serif-accent text-muted">
              Durable photo library — every image you&apos;ve ever uploaded lives here, safely on Emergent object storage (survives every redeploy).
              Search, see where each photo is being used across the catalog, re-attach an orphan to any listing, or strip stale references.
            </div>
            {photoMsg && <div data-testid="photos-msg" className={`text-sm font-serif-accent ${photoMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{photoMsg}</div>}

            {photos === null ? (
              <div className="text-muted py-12 text-center font-serif-accent border border-white/5 rounded-sm">Loading library…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border border-white/10 rounded-sm p-4">
                    <div className="font-display uppercase text-[10px] tracking-[0.2em] text-muted">Total in library</div>
                    <div data-testid="photos-stat-total" className="font-display text-3xl text-white mt-1">{photos.stored}</div>
                  </div>
                  <div className="border border-amber-400/30 rounded-sm p-4 bg-amber-400/5">
                    <div className="font-display uppercase text-[10px] tracking-[0.2em] text-amber-300">Lost (404)</div>
                    <div data-testid="photos-stat-missing" className="font-display text-3xl text-amber-300 mt-1">{photos.missing}</div>
                    <div className="text-[10px] text-muted mt-1">Referenced but not in durable storage.</div>
                  </div>
                  <div className="border border-white/10 rounded-sm p-4">
                    <div className="font-display uppercase text-[10px] tracking-[0.2em] text-muted">Orphans</div>
                    <div className="font-display text-3xl text-white mt-1">{photos.photos.filter(p => !p.missing && p.usage.length === 0).length}</div>
                    <div className="text-[10px] text-muted mt-1">In storage but no listing points to them.</div>
                  </div>
                  <div className="border border-white/10 rounded-sm p-4">
                    <div className="font-display uppercase text-[10px] tracking-[0.2em] text-muted">Total bytes</div>
                    <div className="font-display text-3xl text-white mt-1">{(photos.photos.reduce((a, p) => a + (p.size || 0), 0) / (1024 * 1024)).toFixed(1)}<span className="text-sm text-muted ml-1">MB</span></div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-b border-white/5 pb-3">
                  <input
                    data-testid="photos-search"
                    value={photoSearch}
                    onChange={(e) => setPhotoSearch(e.target.value)}
                    placeholder="Search by filename or business name…"
                    className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm flex-1 min-w-[200px]"
                  />
                  {[
                    { id: "all", label: "All" },
                    { id: "stored", label: "In storage" },
                    { id: "missing", label: "Lost" },
                    { id: "orphan", label: "Orphans" },
                    { id: "used", label: "In use" },
                  ].map(f => (
                    <button
                      key={f.id}
                      data-testid={`photos-filter-${f.id}`}
                      onClick={() => setPhotoFilter(f.id)}
                      className={`px-3 py-2 font-display uppercase text-[10px] tracking-[0.2em] rounded-sm border ${photoFilter === f.id ? "border-gold text-gold" : "border-white/10 text-muted hover:text-white"}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {photos.photos
                    .filter(p => {
                      if (photoFilter === "stored" && p.missing) return false;
                      if (photoFilter === "missing" && !p.missing) return false;
                      if (photoFilter === "orphan" && (p.missing || p.usage.length > 0)) return false;
                      if (photoFilter === "used" && p.usage.length === 0) return false;
                      if (!photoSearch.trim()) return true;
                      const q = photoSearch.trim().toLowerCase();
                      return p.filename.toLowerCase().includes(q)
                        || p.usage.some(u => (u.display || "").toLowerCase().includes(q));
                    })
                    .map(p => (
                      <div
                        key={p.filename}
                        data-testid={`photo-card-${p.filename}`}
                        className={`border rounded-sm overflow-hidden bg-surface/40 ${p.missing ? "border-amber-400/40" : "border-white/10 hover:border-gold/40"} transition-colors`}
                      >
                        {p.missing ? (
                          <div className="aspect-square bg-amber-400/5 flex flex-col items-center justify-center text-amber-300 p-4 text-center">
                            <div className="font-display text-4xl">404</div>
                            <div className="text-[10px] uppercase tracking-widest font-display mt-1">File lost — needs re-upload</div>
                          </div>
                        ) : (
                          <div
                            className="aspect-square bg-cover bg-center bg-bg/40"
                            style={{ backgroundImage: `url('${BACKEND_URL}${p.url}')` }}
                          />
                        )}
                        <div className="p-3 space-y-2">
                          <div className="font-mono text-[10px] text-muted truncate" title={p.filename}>{p.filename}</div>
                          <div className="flex items-center justify-between text-[10px] text-muted">
                            <span>{p.size > 0 ? `${(p.size / 1024).toFixed(0)} KB` : "—"}</span>
                            {p.last_modified && <span>{new Date(p.last_modified).toLocaleDateString()}</span>}
                          </div>
                          {p.usage.length === 0 ? (
                            <div className={`text-[10px] font-display uppercase tracking-widest ${p.missing ? "text-amber-300" : "text-muted/60"}`}>
                              {p.missing ? "Lost reference" : "Orphan — not attached"}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className="text-[10px] font-display uppercase tracking-widest text-gold">Used by · {p.usage.length}</div>
                              {p.usage.slice(0, 3).map((u, i) => (
                                <div key={i} className="text-[11px] text-white/80 truncate" title={`${u.label}: ${u.display}`}>
                                  · <span className="text-muted">{u.label}:</span> {u.display}
                                </div>
                              ))}
                              {p.usage.length > 3 && <div className="text-[10px] text-muted">+ {p.usage.length - 3} more</div>}
                            </div>
                          )}
                          <div className="flex gap-2 pt-2">
                            {!p.missing && (
                              <button
                                data-testid={`photo-attach-${p.filename}`}
                                onClick={() => setPhotoAttachTarget({ filename: p.filename })}
                                disabled={photoBusy}
                                className="flex-1 font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-white/15 hover:border-gold hover:text-gold rounded-sm disabled:opacity-50"
                              >
                                Attach…
                              </button>
                            )}
                            <button
                              data-testid={`photo-strip-${p.filename}`}
                              onClick={() => deletePhotoRefs(p.filename)}
                              disabled={photoBusy || p.usage.length === 0}
                              className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-white/15 hover:border-red-400 hover:text-red-400 rounded-sm disabled:opacity-30"
                              title={p.usage.length === 0 ? "Nothing referencing this photo" : "Strip all catalog references"}
                            >
                              Strip refs
                            </button>
                            {!p.missing && (
                              <button
                                data-testid={`photo-delete-${p.filename}`}
                                onClick={() => hardDeletePhoto(p.filename)}
                                disabled={photoBusy}
                                className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-red-500/40 hover:bg-red-500/20 hover:border-red-400 text-red-400 rounded-sm disabled:opacity-30"
                                title="Permanently remove from library"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>

                {photos.photos.length === 0 && (
                  <div className="text-muted py-16 text-center font-serif-accent border border-white/5 rounded-sm">
                    Library is empty. Photos appear here automatically as you upload them anywhere in the admin.
                  </div>
                )}
              </>
            )}

            {photoAttachTarget && (
              <div data-testid="photo-attach-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={() => setPhotoAttachTarget(null)}>
                <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-gold/30 rounded-sm max-w-2xl w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto">
                  <div className="flex items-start gap-4">
                    <div className="w-24 h-24 shrink-0 bg-cover bg-center rounded-sm border border-white/10" style={{ backgroundImage: `url('${BACKEND_URL}/api/uploads/${photoAttachTarget.filename}')` }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase text-base tracking-tight">Attach photo</div>
                      <div className="font-mono text-[10px] text-muted mt-1 truncate">{photoAttachTarget.filename}</div>
                    </div>
                    <button onClick={() => setPhotoAttachTarget(null)} className="text-muted hover:text-white text-2xl leading-none">×</button>
                  </div>

                  {/* Target kind switcher */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      { k: "business",          label: "Business" },
                      { k: "category_override", label: "Category" },
                      { k: "popup_ad",          label: "Pop-up" },
                      { k: "ad",                label: "24/7 Ad" },
                      { k: "banner_ad",         label: "Banner" },
                      { k: "carousel_ad",       label: "Carousel" },
                      { k: "happening",         label: "Happening" },
                    ].map(opt => (
                      <button
                        key={opt.k}
                        data-testid={`photo-attach-kind-${opt.k}`}
                        onClick={() => setPhotoAttachTarget({ ...photoAttachTarget, kind: opt.k })}
                        className={`px-3 py-2 font-display uppercase text-[10px] tracking-[0.2em] rounded-sm border ${(photoAttachTarget.kind || "business") === opt.k ? "border-gold text-gold" : "border-white/10 text-muted hover:text-white"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Target list — picker varies by kind */}
                  <PhotoAttachTargetPicker
                    kind={photoAttachTarget.kind || "business"}
                    onPick={(target_id) => attachPhotoToTarget(photoAttachTarget.filename, photoAttachTarget.kind || "business", target_id)}
                    busy={photoBusy}
                    bizs={bizs}
                    bizSearch={bizSearch}
                    setBizSearch={setBizSearch}
                    headers={headers}
                  />
                </div>
              </div>
            )}
          </div>
        )}



        {tab === "emails" && (
          <div className="space-y-4" data-testid="emails-tab">
            <div className="font-serif-accent text-muted">Every email captured across the site — the global newsletter list and per-business subscribers. Export any list to CSV for use with your ESP.</div>

            {(newsletterSubs || bizEmailCaptures) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-white/10 rounded-sm p-4">
                  <div className="font-display uppercase text-[10px] tracking-[0.2em] text-muted">Newsletter subscribers</div>
                  <div data-testid="emails-newsletter-count" className="font-display text-3xl mt-1">{newsletterSubs?.count ?? "—"}</div>
                </div>
                <div className="border border-white/10 rounded-sm p-4">
                  <div className="font-display uppercase text-[10px] tracking-[0.2em] text-muted">Business page captures</div>
                  <div data-testid="emails-biz-count" className="font-display text-3xl mt-1">{bizEmailCaptures?.count ?? "—"}</div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-b border-white/5 pb-3">
              {[
                { id: "newsletter", label: "Newsletter (global)" },
                { id: "business",   label: "Per-business captures" },
              ].map(t => (
                <button
                  key={t.id}
                  data-testid={`emails-subtab-${t.id}`}
                  onClick={() => setEmailsSubTab(t.id)}
                  className={`px-3 py-2 font-display uppercase text-[10px] tracking-[0.2em] rounded-sm border ${emailsSubTab === t.id ? "border-gold text-gold" : "border-white/10 text-muted hover:text-white"}`}
                >
                  {t.label}
                </button>
              ))}
              <input
                data-testid="emails-search"
                value={emailsSearch}
                onChange={(e) => setEmailsSearch(e.target.value)}
                placeholder="Search email or business…"
                className="ml-auto bg-bg border border-white/10 p-2.5 rounded-sm text-sm min-w-[200px]"
              />
              {(emailsSubTab === "newsletter" ? (newsletterSubs?.subscribers?.length || 0) : (bizEmailCaptures?.subscribers?.length || 0)) > 0 && (
                <button
                  data-testid="emails-csv"
                  onClick={() => {
                    if (emailsSubTab === "newsletter") downloadEmailCsv(newsletterSubs.subscribers, "newsletter-subscribers.csv");
                    else downloadEmailCsv(bizEmailCaptures.subscribers, "business-email-captures.csv");
                  }}
                  className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-gold/40 text-gold hover:bg-gold hover:text-bg rounded-sm"
                >
                  ⬇ Export CSV
                </button>
              )}
            </div>

            {emailsSubTab === "newsletter" && (
              !newsletterSubs ? <div className="text-muted text-center py-8 font-serif-accent">Loading…</div>
              : newsletterSubs.subscribers.length === 0 ? <div className="text-muted text-center py-10 border border-white/5 rounded-sm font-serif-accent">No newsletter subscribers yet.</div>
              : (
                <div className="border border-white/10 rounded-sm overflow-hidden">
                  <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 bg-bg/50 text-[10px] uppercase tracking-[0.22em] text-muted font-display">
                    <div className="col-span-7">Email</div>
                    <div className="col-span-3">Source</div>
                    <div className="col-span-2 text-right">Subscribed</div>
                  </div>
                  {newsletterSubs.subscribers
                    .filter(s => !emailsSearch.trim() || (s.email || "").toLowerCase().includes(emailsSearch.toLowerCase()))
                    .slice(0, 500)
                    .map(s => (
                      <div key={s.email + (s.subscribed_at || "")} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm">
                        <div className="col-span-7 truncate">{s.email}</div>
                        <div className="col-span-3 text-muted text-xs font-serif-accent">{s.source || s.locale || "site"}</div>
                        <div className="col-span-2 text-right text-muted text-xs font-mono">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : "—"}</div>
                      </div>
                    ))}
                </div>
              )
            )}

            {emailsSubTab === "business" && (
              !bizEmailCaptures ? <div className="text-muted text-center py-8 font-serif-accent">Loading…</div>
              : bizEmailCaptures.subscribers.length === 0 ? <div className="text-muted text-center py-10 border border-white/5 rounded-sm font-serif-accent">No business page captures yet.</div>
              : (
                <div className="border border-white/10 rounded-sm overflow-hidden">
                  <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 bg-bg/50 text-[10px] uppercase tracking-[0.22em] text-muted font-display">
                    <div className="col-span-5">Email</div>
                    <div className="col-span-5">Business</div>
                    <div className="col-span-2 text-right">Date</div>
                  </div>
                  {bizEmailCaptures.subscribers
                    .filter(s => {
                      if (!emailsSearch.trim()) return true;
                      const q = emailsSearch.toLowerCase();
                      return (s.email || "").toLowerCase().includes(q) || (s.business_name || "").toLowerCase().includes(q);
                    })
                    .slice(0, 500)
                    .map(s => (
                      <div key={s.id || (s.email + s.business_id)} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm">
                        <div className="col-span-5 truncate">{s.email}</div>
                        <div className="col-span-5 truncate">{s.business_name}</div>
                        <div className="col-span-2 text-right text-muted text-xs font-mono">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</div>
                      </div>
                    ))}
                </div>
              )
            )}
          </div>
        )}



        {tab === "casino_subs" && (
          <div className="space-y-5" data-testid="casino-subs-tab">
            <div className="font-serif-accent text-muted">Enterprise multi-business tier for casinos ($2,499/mo, up to 5 businesses each with team-member access).</div>
            {casinoMsg && <div className={`text-sm font-serif-accent ${casinoMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{casinoMsg}</div>}

            <div className="border border-gold/30 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent space-y-3">
              <div className="font-display uppercase text-base tracking-tight">+ Create a casino subscription</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input data-testid="casino-create-owner" value={casinoCreate.owner_user_id} onChange={(e) => setCasinoCreate({ ...casinoCreate, owner_user_id: e.target.value.trim() })} placeholder="Owner user_id (from /api/auth/me)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input data-testid="casino-create-name" value={casinoCreate.company_name} onChange={(e) => setCasinoCreate({ ...casinoCreate, company_name: e.target.value })} placeholder="Company name" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input data-testid="casino-create-limit" type="number" min={1} max={50} value={casinoCreate.business_limit} onChange={(e) => setCasinoCreate({ ...casinoCreate, business_limit: parseInt(e.target.value) || 5 })} placeholder="Business limit" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              </div>
              <button data-testid="casino-create-btn" disabled={casinoBusy || !casinoCreate.owner_user_id} onClick={createCasinoSub} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-40">
                {casinoBusy ? "Creating…" : "Create subscription"}
              </button>
            </div>

            <div className="space-y-3">
              {casinoSubs === null && <div className="text-muted text-sm">Tap Reload above to load.</div>}
              {casinoSubs !== null && casinoSubs.length === 0 && <div className="text-muted text-sm">No casino subscriptions yet.</div>}
              {(casinoSubs || []).map(sub => (
                <div key={sub.id} data-testid={`casino-sub-row-${sub.id}`} className="border border-white/10 rounded-sm p-4 bg-bg">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-display uppercase text-base tracking-tight">{sub.company_name || "—"}</div>
                      <div className="text-muted text-[11px] mt-1">
                        id: <code className="bg-surface px-1">{sub.id}</code> · owner: <code className="bg-surface px-1">{sub.owner_user_id}</code>
                      </div>
                      <div className="text-muted text-[11px] mt-1">
                        {(sub.business_ids || []).length} of {sub.business_limit} businesses · {sub.team_member_count} team members · status: {sub.status}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select data-testid={`casino-sub-status-${sub.id}`} value={sub.status} onChange={(e) => patchCasinoSub(sub.id, { status: e.target.value })} className="bg-surface border border-white/10 p-2 rounded-sm text-xs">
                        <option value="active">active</option>
                        <option value="past_due">past_due</option>
                        <option value="canceled">canceled</option>
                      </select>
                      <input type="number" min={1} max={50} defaultValue={sub.business_limit} onBlur={(e) => { const n = parseInt(e.target.value); if (n && n !== sub.business_limit) patchCasinoSub(sub.id, { business_limit: n }); }} className="w-20 bg-surface border border-white/10 p-2 rounded-sm text-xs" title="Business limit" />
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Attached businesses</div>
                    <div className="grid gap-1">
                      {(sub.businesses || []).map(b => (
                        <div key={b.id} className="flex items-center justify-between text-sm p-2 bg-surface rounded-sm">
                          <span>{b.name}</span>
                          <button data-testid={`casino-detach-${sub.id}-${b.id}`} onClick={() => detachBiz(sub.id, b.id)} className="text-[10px] font-display uppercase tracking-[0.2em] px-2 py-1 border border-red-400/40 text-red-300 rounded-sm hover:bg-red-400/10">Detach</button>
                        </div>
                      ))}
                      {(!sub.businesses || sub.businesses.length === 0) && <div className="text-muted text-xs">None yet.</div>}
                    </div>

                    <div className="mt-2 flex items-center gap-2">
                      <select data-testid={`casino-attach-select-${sub.id}`} value={attachSelect[sub.id] || ""} onChange={(e) => setAttachSelect(prev => ({ ...prev, [sub.id]: e.target.value }))} className="bg-surface border border-white/10 p-2 rounded-sm text-xs flex-1">
                        <option value="">— pick a business to attach —</option>
                        {(bizs || []).filter(b => !(sub.business_ids || []).includes(b.id)).map(b => (
                          <option key={b.id} value={b.id}>{b.name} ({b.category})</option>
                        ))}
                      </select>
                      <button data-testid={`casino-attach-btn-${sub.id}`} disabled={!attachSelect[sub.id]} onClick={() => attachBiz(sub.id)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-40">+ Attach</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "categories" && (
          <div className="space-y-4" data-testid="categories-tab">
            <div className="font-serif-accent text-muted">Manage homepage categories — change image, rename, reorder, or add a brand-new category. Changes are live immediately.</div>
            {catMsg && <div className={`text-sm font-serif-accent ${catMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{catMsg}</div>}

            <div className="border border-gold/30 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent space-y-3">
              <div className="font-display uppercase text-base tracking-tight">+ Add a new category</div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="new-cat-key" value={catNew.key} onChange={(e) => setCatNew({ ...catNew, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} placeholder="key (lowercase, no spaces — underscores ok)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input data-testid="new-cat-title" value={catNew.title} onChange={(e) => setCatNew({ ...catNew, title: e.target.value })} placeholder="Display title" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              </div>
              <input data-testid="new-cat-tag" value={catNew.tag} onChange={(e) => setCatNew({ ...catNew, tag: e.target.value })} placeholder="Tag (small text under title)" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              <input data-testid="new-cat-subcategories" value={catNew.subcategories} onChange={(e) => setCatNew({ ...catNew, subcategories: e.target.value })} placeholder="Sub-categories (comma-separated, optional) — e.g. Strip, Off-Strip, Downtown" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              <ImageUploader value={catNew.img} onChange={(v) => setCatNew({ ...catNew, img: v })} />
              <CategoryCardPreview img={catNew.img} title={catNew.title} tag={catNew.tag} testId="new-cat-preview" />
              <button data-testid="new-cat-submit" disabled={catBusy} onClick={addCustomCategory} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">Add category</button>
            </div>

            {catalogue === null ? (
              <div className="text-muted py-6 text-center font-serif-accent">Loading…</div>
            ) : (
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="px-4 py-2 bg-bg/50 text-muted text-xs">{catalogue.length} categories · drag the up/down arrows to reorder</div>
                <div className="max-h-[70vh] overflow-y-auto">
                  {catalogue.map((c, idx) => (
                    <div key={c.key} data-testid={`cat-row-${c.key}`} className="flex items-start gap-3 p-3 border-b border-white/5 hover:bg-bg/30">
                      <div className="flex flex-col gap-1 shrink-0">
                        <button data-testid={`cat-up-${c.key}`} onClick={() => moveCategory(idx, -1)} disabled={idx === 0} className="px-2 py-0.5 border border-white/10 rounded-sm text-xs hover:border-gold disabled:opacity-30">▲</button>
                        <button data-testid={`cat-down-${c.key}`} onClick={() => moveCategory(idx, 1)} disabled={idx === catalogue.length - 1} className="px-2 py-0.5 border border-white/10 rounded-sm text-xs hover:border-gold disabled:opacity-30">▼</button>
                      </div>
                      <div className="w-20 h-16 shrink-0 rounded-sm bg-cover bg-center bg-bg" style={{ backgroundImage: `url('${c.img || ""}')` }} />
                      {catEditing?.key === c.key ? (
                        <div className="flex-1 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <input value={catEditing.title} onChange={(e) => setCatEditing({ ...catEditing, title: e.target.value })} placeholder="Title" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" data-testid={`cat-edit-title-${c.key}`} />
                            <input value={catEditing.tag || ""} onChange={(e) => setCatEditing({ ...catEditing, tag: e.target.value })} placeholder="Tag" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display mb-1">Sub-categories</div>
                            <input
                              data-testid={`cat-edit-subs-${c.key}`}
                              value={catEditing._subs_csv ?? (Array.isArray(catEditing.subcategories) ? catEditing.subcategories.join(", ") : "")}
                              onChange={(e) => setCatEditing({ ...catEditing, _subs_csv: e.target.value })}
                              placeholder="e.g. Strip, Off-Strip, Downtown (comma-separated)"
                              className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm"
                            />
                            <div className="text-[9px] text-muted/70 mt-1 font-serif-accent">Edit to add new or remove existing sub-categories. Applies to both built-in and custom categories.</div>
                          </div>
                          <ImageUploader value={catEditing.img || ""} onChange={(v) => setCatEditing({ ...catEditing, img: v })} />
                          <CategoryCardPreview img={catEditing.img || ""} title={catEditing.title} tag={catEditing.tag} testId={`cat-edit-preview-${c.key}`} />
                          <div className="flex gap-2">
                            <button data-testid={`cat-save-${c.key}`} onClick={() => saveCategoryEdit(catEditing)} disabled={catBusy} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm disabled:opacity-50">Save</button>
                            <button onClick={() => setCatEditing(null)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-white/15 rounded-sm">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <div className="font-display text-sm truncate flex items-center gap-2">
                              {c.title}
                              {c.custom && <span className="text-[9px] uppercase tracking-widest font-display px-1.5 py-0.5 bg-gold/20 text-gold rounded-sm">Custom</span>}
                            </div>
                            <div className="text-muted text-xs truncate">{c.key} · pos {c.position}{c.tag ? ` · ${c.tag}` : ""}</div>
                          </div>
                          <div className="flex flex-col gap-1.5 shrink-0">
                            <button data-testid={`cat-edit-${c.key}`} onClick={() => setCatEditing({ ...c })} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/15 hover:border-gold hover:text-gold rounded-sm">Edit</button>
                            <button data-testid={`cat-delete-${c.key}`} onClick={() => deleteCategoryOverride(c.key, !!c.custom)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/15 hover:border-red-400 hover:text-red-400 rounded-sm">{c.custom ? "Delete" : "Reset"}</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "pricing" && pricing !== null && (
          <div className="space-y-5" data-testid="pricing-tab">
            <div className="font-display uppercase text-lg tracking-tight">Edit prices</div>
            <div className="text-muted text-xs">New prices apply on the next checkout. Existing subscriptions keep their original price.</div>

            <div className="border border-gold/30 rounded-sm p-5 space-y-4">
              <div className="font-serif-accent text-gold text-sm">Subscription tiers (USD/month)</div>
              {["basic", "standard", "pro", "casino"].map(tier => {
                const e = pricing.subscriptions[tier] || { price: 0, discount_pct: 0 };
                const discount_on = (e.discount_pct || 0) > 0;
                const effective = +(e.price * (1 - (e.discount_pct || 0) / 100)).toFixed(2);
                const setEntry = (patch) => setPricing({ ...pricing, subscriptions: { ...pricing.subscriptions, [tier]: { ...e, ...patch } } });
                return (
                  <div key={tier} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-2 uppercase font-display text-sm tracking-[0.18em]">{tier}</div>
                    <div className="col-span-3 flex items-center gap-1">
                      <span className="text-muted text-sm">$</span>
                      <input
                        data-testid={`pricing-sub-${tier}`}
                        type="number" min="1" step="1"
                        value={e.price === 0 ? "" : e.price}
                        onChange={(ev) => setEntry({ price: ev.target.value === "" ? 0 : parseFloat(ev.target.value) })}
                        className="flex-1 bg-bg border border-white/10 p-2.5 rounded-sm text-sm w-full"
                      />
                    </div>
                    <label className="col-span-2 flex items-center gap-2 text-xs">
                      <input data-testid={`pricing-sub-${tier}-discount-toggle`} type="checkbox" checked={discount_on} onChange={(ev) => setEntry({ discount_pct: ev.target.checked ? 20 : 0 })} className="accent-gold" />
                      <span className="text-muted">Discount</span>
                    </label>
                    <select
                      data-testid={`pricing-sub-${tier}-discount-pct`}
                      disabled={!discount_on}
                      value={e.discount_pct || 0}
                      onChange={(ev) => setEntry({ discount_pct: parseFloat(ev.target.value) })}
                      className="col-span-2 bg-bg border border-white/10 p-2 rounded-sm text-sm disabled:opacity-40"
                    >
                      <option value="0">0%</option>
                      <option value="20">20% off</option>
                      <option value="50">50% off</option>
                    </select>
                    <div className="col-span-3 text-right">
                      <span className="text-[10px] text-muted font-display uppercase tracking-widest mr-2">Effective</span>
                      <span data-testid={`pricing-sub-${tier}-effective`} className="text-gold font-display text-sm">${effective}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border border-white/10 rounded-sm p-5 space-y-4">
              <div className="font-serif-accent text-gold text-sm">Ad slot prices (USD)</div>
              {[["popup_ad", "Pop-up ad (14 days)"], ["banner_ad", "Banner ad (28 days)"], ["carousel_ad", "Carousel slot (14 days)"]].map(([k, label]) => {
                const e = pricing[k] || { price: 0, discount_pct: 0 };
                const discount_on = (e.discount_pct || 0) > 0;
                const effective = +(e.price * (1 - (e.discount_pct || 0) / 100)).toFixed(2);
                const setEntry = (patch) => setPricing({ ...pricing, [k]: { ...e, ...patch } });
                return (
                  <div key={k} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3 text-sm">{label}</div>
                    <div className="col-span-2 flex items-center gap-1">
                      <span className="text-muted text-sm">$</span>
                      <input
                        data-testid={`pricing-${k}`}
                        type="number" min="1" step="1"
                        value={e.price === 0 ? "" : e.price}
                        onChange={(ev) => setEntry({ price: ev.target.value === "" ? 0 : parseFloat(ev.target.value) })}
                        className="flex-1 bg-bg border border-white/10 p-2.5 rounded-sm text-sm w-full"
                      />
                    </div>
                    <label className="col-span-2 flex items-center gap-2 text-xs">
                      <input data-testid={`pricing-${k}-discount-toggle`} type="checkbox" checked={discount_on} onChange={(ev) => setEntry({ discount_pct: ev.target.checked ? 20 : 0 })} className="accent-gold" />
                      <span className="text-muted">Discount</span>
                    </label>
                    <select
                      data-testid={`pricing-${k}-discount-pct`}
                      disabled={!discount_on}
                      value={e.discount_pct || 0}
                      onChange={(ev) => setEntry({ discount_pct: parseFloat(ev.target.value) })}
                      className="col-span-2 bg-bg border border-white/10 p-2 rounded-sm text-sm disabled:opacity-40"
                    >
                      <option value="0">0%</option>
                      <option value="20">20% off</option>
                      <option value="50">50% off</option>
                    </select>
                    <div className="col-span-3 text-right">
                      <span className="text-[10px] text-muted font-display uppercase tracking-widest mr-2">Customer pays</span>
                      <span data-testid={`pricing-${k}-effective`} className="text-gold font-display text-sm">${effective}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <button data-testid="pricing-save-btn" onClick={savePricing} className="font-display uppercase tracking-[0.2em] text-xs py-3 px-6 bg-gold-grad text-bg rounded-sm hover:brightness-110">Save pricing</button>
            {pricingMsg && <div className={`text-sm font-serif-accent ${pricingMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{pricingMsg}</div>}
          </div>
        )}

        {tab === "analytics" && (
          <div className="space-y-5" data-testid="analytics-tab">
            <div className="font-serif-accent text-muted">Site-wide visit + engagement counters. Real-time. Refresh with the Reload button.</div>

            {!analytics && <div className="text-muted text-center py-8">Loading…</div>}
            {analytics && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="analytics-cards">
                  {[
                    ["site_visit_total", "Site visits"],
                    ["category_visit_total", "Category visits"],
                    ["ad_impression_total", "Ad impressions"],
                    ["business_clicks", "Business clicks"],
                    ["misc_total", "Other events"],
                  ].map(([k, label]) => (
                    <div key={k} className="border border-gold/30 rounded-sm p-4 bg-gradient-to-br from-gold/5 to-transparent" data-testid={`analytics-${k}`}>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">{label}</div>
                      <div className="font-display text-3xl gold-text mt-1">{(analytics[k] || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div className="border border-white/10 rounded-sm p-5 bg-bg/30">
                    <div className="font-display uppercase text-sm tracking-[0.2em] text-gold mb-3">Visits by category</div>
                    {Object.keys(analytics.category_visits_by_key || {}).length === 0 ? (
                      <div className="text-muted text-sm font-serif-accent">No category clicks recorded yet.</div>
                    ) : (
                      <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                        {Object.entries(analytics.category_visits_by_key).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between text-sm border-b border-white/5 py-1.5" data-testid={`analytics-cat-${k}`}>
                            <span className="text-white capitalize">{k.replace(/_/g, " ")}</span>
                            <span className="font-display text-gold">{v.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="border border-white/10 rounded-sm p-5 bg-bg/30">
                    <div className="font-display uppercase text-sm tracking-[0.2em] text-gold mb-3">Top businesses by clicks</div>
                    {(analytics.top_businesses || []).length === 0 ? (
                      <div className="text-muted text-sm font-serif-accent">No business clicks yet.</div>
                    ) : (
                      <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                        {(analytics.top_businesses || []).map((b) => (
                          <div key={b.id} className="flex items-center justify-between text-sm border-b border-white/5 py-1.5" data-testid={`analytics-biz-${b.id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="text-white truncate">{b.name}</div>
                              <div className="text-[10px] text-muted uppercase tracking-widest">{b.category}</div>
                            </div>
                            <span className="font-display text-gold ml-3">{(b.views_total || 0).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Historic-counter admin tools (bottom of the analytics tab per user request) */}
            <div className="border border-gold/30 rounded-sm p-4 bg-gradient-to-br from-gold/10 to-transparent space-y-3">
              <div>
                <div className="font-display uppercase text-[11px] tracking-[0.2em] text-gold">Boost historic counters</div>
                <div className="text-xs text-muted mt-1">One-shot 1.5× multiply on business views, category-visit counters AND site-visit total. Real traffic keeps counting normally on top. Idempotent per UTC day.</div>
              </div>
              {analyticsBoostMsg && <div data-testid="analytics-boost-msg" className={`text-sm font-serif-accent ${analyticsBoostMsg.startsWith("✓") ? "text-gold" : "text-amber-300"}`}>{analyticsBoostMsg}</div>}
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="analytics-boost-5x"
                  onClick={() => multiplyAnalytics(1.5)}
                  disabled={analyticsBoostBusy}
                  className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50"
                >
                  {analyticsBoostBusy ? "Boosting…" : "Boost views 1.5× now"}
                </button>
                <button
                  data-testid="analytics-seed-30k"
                  onClick={() => seedSiteVisits(10000)}
                  disabled={analyticsBoostBusy}
                  className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-gold/50 text-gold hover:bg-gold hover:text-bg rounded-sm disabled:opacity-50"
                  title="Set site visits to 10,000 (only if currently below)"
                >
                  Seed site visits → 10 000
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div className="space-y-5" data-testid="settings-tab">
            <div className="font-serif-accent text-muted">Site-wide pricing, plan descriptions, founding-member program, Pro tier discount. Changes apply instantly.</div>
            {!settings && <div className="text-muted py-8 text-center">Loading…</div>}
            {settings && (
              <div className="space-y-5">

                {/* Additional category price */}
                <div className="border border-pink-500/40 rounded-sm p-5 bg-gradient-to-br from-pink-500/10 to-transparent">
                  <div className="font-display uppercase text-sm tracking-[0.2em] text-pink-300 mb-2">Additional Category Add-On Price</div>
                  <div className="text-muted text-xs mb-3">Monthly recurring price businesses pay per extra category. Currently <span className="text-white">${(settings.extra_category_price || 9.99).toFixed(2)}/mo</span>.</div>
                  <div className="flex gap-2 items-center">
                    <span className="text-muted">$</span>
                    <input data-testid="settings-extra-cat-price" type="number" min="0.5" max="999" step="0.01" defaultValue={settings.extra_category_price} onChange={(e) => setSettings({ ...settings, extra_category_price: parseFloat(e.target.value || 0) })} className="bg-bg border border-white/10 p-2 rounded-sm text-sm w-32" />
                    <span className="text-muted text-xs uppercase tracking-widest">/month</span>
                    <button data-testid="settings-save-extra-cat" disabled={settingsBusy} onClick={() => saveSettings({ extra_category_price: settings.extra_category_price })} className="ml-auto font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm disabled:opacity-50">Save</button>
                  </div>
                </div>

                {/* Founding member program */}
                <div className="border border-gold/40 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent">
                  <div className="font-display uppercase text-sm tracking-[0.2em] text-gold mb-2">Founding Member Program</div>
                  <div className="text-muted text-xs mb-4">{settings.founding_count} of {settings.founding_cap} subscriptions claimed · <span className={settings.founding_available ? "text-emerald-400" : "text-red-400"}>{settings.founding_available ? "active" : "closed"}</span></div>

                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input data-testid="settings-founding-enabled" type="checkbox" checked={!!settings.founding_enabled} onChange={(e) => setSettings({ ...settings, founding_enabled: e.target.checked })} className="accent-yellow-500" />
                      <span className="text-sm">Founding-Member program enabled</span>
                    </label>
                    <div className="flex gap-2 items-center text-sm">
                      <span className="text-muted">Cap:</span>
                      <input data-testid="settings-founding-cap" type="number" min="1" max="10000" value={settings.founding_cap || 100} onChange={(e) => setSettings({ ...settings, founding_cap: parseInt(e.target.value || 100) })} className="bg-bg border border-white/10 p-2 rounded-sm w-24" />
                      <span className="text-muted text-xs">subscriptions</span>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display mb-1.5">Founding-member blurb (shown on Plans page)</div>
                      <textarea data-testid="settings-founding-blurb" rows={2} value={settings.founding_blurb || ""} onChange={(e) => setSettings({ ...settings, founding_blurb: e.target.value })} className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                    </div>
                    <button data-testid="settings-save-founding" disabled={settingsBusy} onClick={() => saveSettings({ founding_enabled: settings.founding_enabled, founding_cap: settings.founding_cap, founding_blurb: settings.founding_blurb })} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm disabled:opacity-50">Save founding settings</button>
                  </div>
                </div>

                {/* Plan descriptions */}
                <div className="border border-white/10 rounded-sm p-5 bg-bg/30">
                  <div className="font-display uppercase text-sm tracking-[0.2em] text-gold mb-2">Plan descriptions</div>
                  <div className="text-muted text-xs mb-3">Edit the marketing copy shown under each plan title on the Plans page.</div>
                  <div className="space-y-3">
                    {["basic", "standard", "pro"].map((k) => (
                      <div key={k}>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-display mb-1">{k}</div>
                        <textarea data-testid={`settings-desc-${k}`} rows={2} value={(settings.plan_descriptions || {})[k] || ""} onChange={(e) => setSettings({ ...settings, plan_descriptions: { ...(settings.plan_descriptions || {}), [k]: e.target.value } })} className="w-full bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                      </div>
                    ))}
                    <button data-testid="settings-save-descs" disabled={settingsBusy} onClick={() => saveSettings({ plan_descriptions: settings.plan_descriptions })} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm disabled:opacity-50">Save descriptions</button>
                  </div>
                </div>

                {/* Pro ad discount */}
                <div className="border border-cyan-400/40 rounded-sm p-5 bg-gradient-to-br from-cyan-400/10 to-transparent">
                  <div className="font-display uppercase text-sm tracking-[0.2em] text-cyan-300 mb-2">Pro Tier Ad Discount</div>
                  <div className="text-muted text-xs mb-3">Discount applied to every paid ad-space (pop-up, banner, carousel) for Pro subscribers.</div>
                  <div className="flex gap-2 items-center">
                    <input data-testid="settings-pro-ad-discount" type="number" min="0" max="90" step="1" value={settings.pro_ad_discount_pct || 20} onChange={(e) => setSettings({ ...settings, pro_ad_discount_pct: parseFloat(e.target.value || 0) })} className="bg-bg border border-white/10 p-2 rounded-sm text-sm w-24" />
                    <span className="text-muted text-xs uppercase tracking-widest">% off every ad-space</span>
                    <button data-testid="settings-save-pro-discount" disabled={settingsBusy} onClick={() => saveSettings({ pro_ad_discount_pct: settings.pro_ad_discount_pct })} className="ml-auto font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm disabled:opacity-50">Save</button>
                  </div>
                </div>

                {/* Zip City social media (site footer) */}
                <div className="border border-pink-500/40 rounded-sm p-5 bg-gradient-to-br from-pink-500/10 to-transparent space-y-3" data-testid="settings-zc-socials">
                  <div className="font-display uppercase text-sm tracking-[0.2em] text-pink-300">Zip City Social Media (footer)</div>
                  <div className="text-muted text-xs">Links shown as circular icons in the homepage footer. Leave blank to hide an icon (falls back to hardcoded defaults if left null).</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input data-testid="settings-zc-x" value={settings.zc_x || ""} onChange={(e) => setSettings({ ...settings, zc_x: e.target.value })} placeholder="X (Twitter) URL" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                    <input data-testid="settings-zc-instagram" value={settings.zc_instagram || ""} onChange={(e) => setSettings({ ...settings, zc_instagram: e.target.value })} placeholder="Instagram URL" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                    <input data-testid="settings-zc-tiktok" value={settings.zc_tiktok || ""} onChange={(e) => setSettings({ ...settings, zc_tiktok: e.target.value })} placeholder="TikTok URL" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                    <input data-testid="settings-zc-facebook" value={settings.zc_facebook || ""} onChange={(e) => setSettings({ ...settings, zc_facebook: e.target.value })} placeholder="Facebook URL (optional)" className="bg-bg border border-white/10 p-2 rounded-sm text-sm" />
                  </div>
                  <button data-testid="settings-save-zc-socials" disabled={settingsBusy} onClick={() => saveSettings({ zc_x: settings.zc_x || "", zc_instagram: settings.zc_instagram || "", zc_tiktok: settings.zc_tiktok || "", zc_facebook: settings.zc_facebook || "" })} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm disabled:opacity-50">Save socials</button>
                </div>

                {settingsMsg && <div className={`text-sm py-2 px-3 rounded-sm border ${settingsMsg.startsWith("✓") ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5" : "text-red-300 border-red-500/30 bg-red-500/5"}`} data-testid="settings-msg">{settingsMsg}</div>}
              </div>
            )}
          </div>
        )}

        {tab === "team" && adminUsers !== null && (adminMe?.role === "super" || adminMe?.role === "master") && (
          <div className="space-y-5" data-testid="team-tab">
            <div className="border border-gold/40 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent">
              <div className="font-serif-accent text-gold text-sm">~ Add a teammate</div>
              <div className="font-display uppercase text-lg tracking-tight">New admin user</div>
              <div className="text-muted text-xs mt-1">Pick role: <span className="text-white">Staff</span> can review listings, gift subscriptions, run payouts. <span className="text-gold">Super</span> can also create/reset other admins.</div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input data-testid="new-admin-user" value={newAdminUser} onChange={(e) => setNewAdminUser(e.target.value)} placeholder="username" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input data-testid="new-admin-pwd" type="password" value={newAdminPwd} onChange={(e) => setNewAdminPwd(e.target.value)} placeholder="initial password (8+)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <select data-testid="new-admin-role" value={newAdminRole} onChange={(e) => setNewAdminRole(e.target.value)} className="bg-bg border border-white/10 p-3 rounded-sm text-sm">
                  <option value="staff">Staff</option>
                  <option value="super">Super-admin</option>
                </select>
              </div>
              <button data-testid="new-admin-create" onClick={createAdminUser} className="mt-3 font-display uppercase tracking-[0.2em] text-[11px] py-2.5 px-5 bg-gold-grad text-bg rounded-sm hover:brightness-110">Create user</button>
              {teamMsg && <div data-testid="team-msg" className={`text-sm font-serif-accent mt-2 ${teamMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{teamMsg}</div>}
            </div>

            <div className="space-y-2">
              <div className="font-serif-accent text-muted">{adminUsers.length} admin{adminUsers.length === 1 ? "" : "s"} on the team</div>
              {adminUsers.map(u => (
                <div key={u.username} data-testid={`admin-row-${u.username}`} className="border border-white/10 rounded-sm p-4 flex items-center justify-between gap-3 bg-bg/30 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-display uppercase text-sm tracking-[0.18em]">{u.username}</div>
                    <div className="text-muted text-xs mt-0.5">
                      <span className={u.role === "super" ? "text-gold uppercase tracking-[0.2em] font-display text-[10px]" : "uppercase tracking-[0.2em] font-display text-[10px]"}>{u.role}</span>
                      {u.must_change_password && <span className="ml-3 text-amber-400">must change password on next login</span>}
                      <span className="ml-3">created by {u.created_by}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`reset-pwd-${u.username}`} onClick={() => resetAdminPassword(u.username)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-white/15 hover:border-gold hover:text-gold rounded-sm">Reset PW</button>
                    {u.username !== adminMe?.username && (
                      <button data-testid={`delete-admin-${u.username}`} onClick={() => deleteAdminUser(u.username)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* My password */}
            <div className="border border-white/10 rounded-sm p-5 bg-bg/30">
              <div className="font-display uppercase text-sm tracking-[0.18em]">Change my password</div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="my-current-pwd" type="password" value={myCurrPwd} onChange={(e) => setMyCurrPwd(e.target.value)} placeholder="Current password" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input data-testid="my-new-pwd" type="password" value={myNewPwd} onChange={(e) => setMyNewPwd(e.target.value)} placeholder="New password (8+)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              </div>
              <button data-testid="change-my-pwd" onClick={changeMyPassword} className="mt-3 font-display uppercase tracking-[0.2em] text-[11px] py-2.5 px-5 bg-gold-grad text-bg rounded-sm hover:brightness-110">Update password</button>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
